// challenge-chrome — the student Code Challenge editor surface host chrome
// (code-challenge-activity, task 6). Wires the persistent/collapsible
// instructions panel and the Check button: replays the recorded answer key
// (visibly, in the same Run/output area) against the live editor buffer,
// requests a snapshot of the ticked checkpoint variables, compares them, and
// POSTs the result to challengeCheck. Check is unlimited and non-terminal —
// students may keep editing and re-checking after a pass with no penalty.
//
//   wire(ctx) — ctx = { core, payload, trinketId, clientsideMode, isOwner, editorApi, doc }
// editor-core is already constructed (editable) and loaded by main.js.

import { replay } from './challenge-replay.js';
import { compareAll, overallPassed, isTemporalKind, isConsoleRunKind, consoleRunStdinKey,
  relationalExprVariables, isDiscreteKind, gradeStepCheck, gradePromptStepCheck,
  gradeFunctionStepCheck, fnCallFor } from './challenge-grading.js';
// step-checks (Trello IeSYCMgQ): the "Check this step" message copy (pure, host spec).
import { stepCheckMessage, stepCheckSummary, STEP_CHECK_TIMEOUT_LINE, STEP_CHECK_RUNNER_ERROR_LINE }
  from './challenge-step-check.js';
// code-challenge-dotted-path-checkpoints: the SHARED labeling/fallback module
// (also imported by the teacher-only record chrome). resolvePathFallback runs the
// student-side naming-variety fallback at Check time.
import { resolvePathFallback } from './challenge-path-labeling.js';
import { inlineMarkdown } from './inline-markdown.js';
// friendlier-challenges (Trello d7cn4sN9): insert-at-cursor code chips (the click listener is
// wired by main.js) and the pure "better failures" rules for the Check result rows.
import { chipifyHtml, buildChip } from './challenge-insert-chrome.js';
import { lineDiff, diffWindow, firstNearMiss, lookedFor } from './challenge-diff.js';
// tiered-hints (Trello 6WQKXSWl): the shared three-tier hint ladder renderer.
import { renderLadder, usesLadder, buildLadder } from './hint-ladder.js';
import { registerSaveHandler } from './editor-shortcuts.js';
// checker-diagnostics B2 (Trello YyMk8kcZ): the pure "Your game crashed on line N…" formatter.
import { crashMessage } from './check-crash-message.js';
// return-evidence (Trello DyXzvVbP): the per-case "double(5) returned 10 — expected 10 ✓"
// readout under a function-io row. Pure text; rendered below with textContent only.
import { functionEvidence } from './function-evidence.js';

// code-challenge-collision-oscillation-liveness: the full list of top-level vars
// the stepped driver must sample — every checkpoint's own `cp.variable` PLUS the
// actor var names inside a `relational-series` `expr` (which has no `variable`).
// Deduped; `expected-output` has no variable. Exported (code-challenge-causal-
// event-checkpoints task 3.4) so it's directly unit-testable — it's a pure,
// module-level function with no dependency on wire()'s DOM/editor-core closure.
export function collectObservedVariables(checkpoints) {
  var seen = {}, out = [];
  function add(name) {
    if (typeof name === 'string' && name && !seen[name]) { seen[name] = 1; out.push(name); }
  }
  (checkpoints || []).forEach(function (cp) {
    if (!cp || cp.kind === 'expected-output') { return; }
    if (cp.kind === 'relational-series') {
      relationalExprVariables(cp.expr).forEach(add);
      return;
    }
    // code-challenge-causal-event-checkpoints: causal-delta/event-normalized key
    // on `observe`, NOT `variable` — without this branch `add(undefined)` is a
    // silent no-op and the checkpoint's series is never requested from the
    // runner (starves — reads as "not found," not an error). A `relational`-typed
    // event ALSO widens by its `expr`'s referenced variables, same as a
    // standalone `relational-series` checkpoint.
    if (cp.kind === 'causal-delta' || cp.kind === 'event-normalized') {
      add(cp.observe);
      if (cp.event && cp.event.type === 'relational') {
        relationalExprVariables(cp.event.expr).forEach(add);
      }
      return;
    }
    // code-challenge-dotted-path-checkpoints: path-temporal keys on `path`, NOT
    // `variable` — the runner samples it via the safe path resolver.
    if (cp.kind === 'path-temporal') { add(cp.path); return; }
    add(cp.variable);
  });
  return out;
}
import { wireAssets } from './assets-panel.js';
import { initSuggestions } from './suggestions-panel.js';
import { mountPreviewStrip } from './preview-strip.js';
// activity-retry-and-close-on-success (Trello 2eajTgm0): the close-on-success prompt.
import { offerClose, offerReturn } from './close-prompt.js';
// m9-extend-your-game (Trello s5K8qoyn): the ONE definition of "the work this challenge asks
// for is optional", shared with the classroom launch card so the two surfaces can never
// disagree about it. ⚠️ Read that module's header before touching either predicate — the
// count is over checkpoint DEFINITIONS and it excludes `structural` and `changed-from-initial`,
// and getting either half wrong silently regresses a different population of challenges.
import { isAllOptionalChallenge, shouldSuppressCloseOffer }
  from '../../../src/islands/shared/challenge-advisory.js';
// class-due-dates: the page side of a class schedule lock (disable Check when closed).
import { readScheduleLock, disableForLock } from './schedule-lock.js';
// friendlier-challenges (Trello d7cn4sN9): "Use the solution" — the failed-Check counter, the
// offer, the confirm panel, the two POSTs and "Get my code back" (never in editor-api.js).
import { wireSolution } from './challenge-solution.js';
// solution-pieces-merge: the step panel's "Show the solution for this step" + merge cards.
import { createPiecesPanel } from './challenge-pieces.js';
// step-by-step-challenges (Trello nSM5rv4M): the pure rules behind the one-step-at-a-time view.
import { stepModeHonoured, initialStep, stepStates, canGoNext, isLastStep, railText, subSteps }
  from './challenge-steps.js';

// code-challenge-temporal-checkpoints: cold Pyodide boot + up to 1200 stepped
// frames — generous ceiling before a temporal Check is called a run failure.
var TEMPORAL_CHECK_TIMEOUT_MS = 90000;

var AUTOSAVE_DEBOUNCE_MS = 1500;
// Step view: the least scroller height (px) the pinned foot must leave above itself; below it
// the foot is unpinned and follows the step instead of covering the dots, pieces or Merge.
export var FOOT_MIN_ROOM_PX = 96;
// step-result-reach: how much of the instructions banner's top (its toggle row + Read aloud
// button) the grown drawer leaves uncovered, and the slack added to the measured span.
export var DRAWER_BANNER_KEEP_PX = 48;
export var DRAWER_REACH_SLACK_PX = 4;

// one-tab-topbar (Trello 3h9FqNWJ): the wired page's flush, or null before `wire` runs —
// mirroring `assignment-chrome.js`'s `activeFlush`.
var activeFlush = null;

// one-tab-topbar: cancel the autosave debounce and save the live files NOW — the SAME contract
// as `assignment-chrome.js`'s `flushPendingSave()`. Resolves the draft endpoint's reply
// (`{ success: true }` when it saved — `project.draft` swallows errors into a 200, so a caller
// that needs the write checks `success`), rejects on a network/HTTP failure, and resolves `null`
// when no challenge surface is wired. The activity top bar's Back awaits it.
export function flushPendingSave() {
  return activeFlush ? activeFlush() : Promise.resolve(null);
}

// one-tab-topbar: EXPLICIT save state (see `assignment-chrome.js` for the full rule) —
// `pending` (debounce scheduled), `inflight` (save requests outstanding), `lastFailed` (the last
// save to finish rejected or resolved `success !== true`, cleared by the next success).
var saveState = { wired: false, pending: false, inflight: 0, lastFailed: false };

export function hasPendingSave() {
  if (!saveState.wired) { return false; }
  return saveState.pending || saveState.inflight > 0 || saveState.lastFailed;
}

function trackSave(promise) {
  saveState.inflight += 1;
  return Promise.resolve(promise).then(function (res) {
    saveState.inflight = Math.max(0, saveState.inflight - 1);
    saveState.lastFailed = !(res && res.success === true);
    return res;
  }, function (err) {
    saveState.inflight = Math.max(0, saveState.inflight - 1);
    saveState.lastFailed = true;
    throw err;
  });
}

/** Test-only reset so module state does not leak between specs. */
export function __resetChallengeSaveState() {
  activeFlush = null;
  saveState = { wired: false, pending: false, inflight: 0, lastFailed: false };
}

export function wire(ctx) {
  var document = ctx.doc;
  var core = ctx.core;
  var api = ctx.editorApi;
  var payload = ctx.payload;
  var body = document.body;

  // teacher-preview-split (tasks 9.4/9.3 — R24): project-keyed and owner-gated by
  // `helpers.requireProjectCreator`, so preview state comes from `Snippet.isPreview` on the
  // working copy (threaded via `assignmentSurfaceData` into the template), never a URL flag.
  // `challengeCheck` MAY write to that copy — it IS the sanctioned throwaway artefact — but
  // no student-progress record is created, and the copy is excluded from every teacher
  // submission and progress query.
  //
  // teacher-preview-cleanup (finding 2): reached SAME-TAB from the classroom, so the exit
  // NAVIGATES to the classroom URL the classroom itself supplied (`data-return-url`, with the
  // preview flag already stripped) — `history.back()` returned to that URL still carrying
  // `?preview=student`, so it re-entered preview. With no valid target (a bookmarked or pasted
  // embed URL) the strip renders with NO exit button: this surface's flag is `Snippet.isPreview`,
  // so a reload without a query flag changes nothing and would be a dead control.
  //
  // one-tab-topbar (Trello 3h9FqNWJ): where the activity top bar is rendered, ITS Back and
  // preview chip replace the strip. The guard keys on the bar's markup only, so a preview render
  // without the bar keeps the strip.
  if (!document.getElementById('surface-topbar-back')) {
    mountPreviewStrip(document, {
      active: body.getAttribute('data-preview') === 'true',
      exit: 'navigate',
      exitUrl: body.getAttribute('data-return-url') || ''
    });
  }

  var answerKey = payload.challengeAnswerKey || { recordedInputs: [], checkpoints: [] };
  var challengeSeed = payload.challengeSeed;
  var checkpoints = answerKey.checkpoints || [];
  var recordedInputs = answerKey.recordedInputs || [];
  // code-challenge-modify-extend (v2): a PRIMM "Modify / extend" challenge shows a
  // right-edge "What to add" drawer of teacher-authored requirements — GUIDANCE,
  // decoupled from the graded checkpoints. Students self-tick rows as their own
  // progress; the ticks persist on their attempt (checklistTicks) and NEVER gate
  // grading (Check still grades the recorded answer key). Requirements + the saved
  // ticks ride in the same page payload as the answer key.
  var modifyMode = answerKey.mode === 'modify';
  var requirements = Array.isArray(answerKey.requirements) ? answerKey.requirements : [];
  var savedTicks = Array.isArray(payload.checklistTicks) ? payload.checklistTicks : [];
  // friendlier-challenges (decisions Round 4): chips insert nothing — and say so with
  // aria-disabled — for a viewer who is not the work's owner (a teacher looking at a student's
  // copy) or on a read-only editor.
  var chipsDisabled = ctx.isOwner !== true ||
    !!(core && typeof core.isReadOnly === 'function' && core.isReadOnly());
  // m9-extend-your-game: is every requirement in the drawer covered by an OPTIONAL extension?
  // ⚠️ NOT "every checkpoint here is advisory" — the grader awards no pass without a gating
  // row, so that test is false for the very activity this exists for and would ship nothing.
  // Resolved ONCE, from the one shared helper, so the drawer framing and the countdown
  // suppression can never drift apart.
  var allOptional = isAllOptionalChallenge(checkpoints, requirements.length);
  // code-challenge-temporal-checkpoints: the challenge-level frame budget rides in
  // the answer-key payload (project.js). Pygame Zero challenges with any temporal
  // checkpoint grade via the deterministic stepped engine instead of a replay.
  var frameCap = answerKey.frameCap;
  var hasTemporal = ctx.clientsideMode === '' &&
    checkpoints.some(function (cp) { return isTemporalKind(cp.kind); });
  // code-challenge-behavioral-structural: a console (`python`) challenge that
  // carries ANY behavioural/structural checkpoint grades via the instrumented
  // trace pass (requestChallengeTrace) instead of the replay+snapshot path.
  var hasTrace = ctx.clientsideMode === 'python' &&
    checkpoints.some(function (cp) { return cp.kind === 'behavioral' || cp.kind === 'structural'; });
  // code-challenge-function-io: a console (`python`) challenge that carries any
  // `function-io` checkpoint grades those via the shared function-call pass
  // (requestFunctionCall). It may COEXIST with expected-output/behavioural/
  // structural/plain-variable kinds — each kind is graded ONLY from its own
  // family's pass (never from the fncall pass's __name__!="__main__" stdout).
  var hasFunctionIO = ctx.clientsideMode === 'python' &&
    checkpoints.some(function (cp) { return cp.kind === 'function-io'; });
  // code-challenge-exception-checkpoints: a console (`python`) challenge that
  // carries any exception/error-behaviour checkpoint (runs-clean/raises-on/
  // handles-gracefully) grades those via the shared canned-stdin console-run pass
  // (requestConsoleRun). Because runs-clean is auto-ticked whenever the reference
  // ran clean, this coexists with function-io/expected-output/behavioural in the
  // COMMON case — the unified runConsoleCheck() collects EVERY required pass so no
  // family is silently misgraded.
  // code-challenge-stdin-transcript (grading v3 [3/3]): a `stdin-transcript`
  // checkpoint ALSO grades via the shared canned-stdin console-run pass (it reads
  // consoleRunData.byStdin), but the grader routes it to compareStdinTranscript
  // (NOT compareConsoleRun) — so widen the ORCHESTRATOR predicate here, never
  // isConsoleRunKind in the grader. A `stdin-transcript`-only challenge has no
  // auto-ticked runs-clean, so without this it would fall to the legacy
  // replay+snapshot path and silently misgrade.
  function needsConsolePass(cp) {
    return !!cp && (isConsoleRunKind(cp.kind) || cp.kind === 'stdin-transcript');
  }
  var hasConsoleRun = ctx.clientsideMode === 'python' &&
    checkpoints.some(needsConsolePass);

  var attemptId = body.getAttribute('data-draft-project-id') ||
    body.getAttribute('data-project-id') || '';
  var courseCtx = {
    courseId: body.getAttribute('data-course-id') || '',
    lessonId: body.getAttribute('data-lesson-id') || '',
    materialId: body.getAttribute('data-material-id') || ''
  };

  // Teacher code suggestions: render any PENDING suggestion as a side-by-side diff with
  // Accept/Dismiss (rebuild of the phase-3 UI dropped in the editor-surface migration).
  initSuggestions({ projectId: attemptId, doc: document });

  var checkBtn = document.getElementById('challenge-check');
  var scheduleLock = readScheduleLock(document);
  var resultSection = document.getElementById('challenge-result');
  var resultTitle = document.getElementById('challenge-result-title');
  var resultSub = document.getElementById('challenge-result-sub');
  var resultBadge = document.getElementById('challenge-result-badge');
  var resultProgress = resultSection && resultSection.querySelector('.cr-progress > i');
  var resultList = document.getElementById('challenge-result-list');
  var resultFoot = document.getElementById('challenge-result-foot');
  var resultClose = document.getElementById('challenge-result-close');
  var resultAgain = document.getElementById('challenge-result-again');
  var resultBack = document.getElementById('challenge-result-back');

  // tiered-hints: the challenge-level ladder (every challenge mode). ALWAYS ladder mode — there
  // is no legacy look for it — even with only Hint 1. Built ONCE per page load into its own
  // container, a SIBLING right after `#challenge-result-list` (that list is `aria-live="polite"`
  // and re-rendered on every Check, so a ladder inside it would be re-announced and would nest
  // live regions). From the first failed Check on the container is shown, so what the student
  // revealed is kept across Checks. Hidden on a pass, the checking spinner, or an error.
  var challengeLadderEntry = { hint: answerKey.hint, hintVideo: answerKey.hintVideo, hintPartial: answerKey.hintPartial };
  var ladderBox = document.getElementById('challenge-result-ladder');
  var challengeLadderNode = null;
  if (ladderBox && buildLadder(challengeLadderEntry).length) {
    challengeLadderNode = document.createElement('div');
    challengeLadderNode.className = 'hl-result';
    challengeLadderNode.appendChild(renderLadder(document, challengeLadderEntry, {
      renderText: function (target, text) { target.innerHTML = inlineMarkdown(String(text).trim()); }
    }));
    ladderBox.appendChild(challengeLadderNode);
  }
  function showLadder(on) { if (ladderBox && challengeLadderNode) { ladderBox.hidden = !on; } }

  // --- Autosave-to-Draft, exactly like the assignment surface (assignment-chrome.js) —
  // Check grades the LIVE buffer directly (no separate save step), but the
  // student's in-progress work still needs to persist across sessions/reloads.
  var saveStatus = document.getElementById('challenge-save-status');
  function setSaveStatus(text, isError) {
    if (!saveStatus) { return; }
    saveStatus.textContent = text || '';
    saveStatus.classList.toggle('runner-error', !!isError);
  }
  var saveTimer = null;
  function scheduleSave() {
    if (saveTimer) { clearTimeout(saveTimer); }
    setSaveStatus('Editing…', false);
    saveState.pending = true;
    saveTimer = setTimeout(function () {
      saveTimer = null;
      saveState.pending = false;
      setSaveStatus('Saving…', false);
      trackSave(api.saveDraft(attemptId, core.getFiles()))
        .then(function () { setSaveStatus('Saved', false); })
        .catch(function (err) { setSaveStatus('Save failed', true); console.error(err); });
    }, AUTOSAVE_DEBOUNCE_MS);
  }
  var editorInstance = core.getEditor();
  if (editorInstance && editorInstance.on) {
    editorInstance.on('change', scheduleSave);
  }
  // one-tab-topbar: the save state is live from here.
  saveState.wired = true;

  // one-tab-topbar: the exported flush (see `flushPendingSave` above).
  activeFlush = function () {
    if (saveTimer) { clearTimeout(saveTimer); saveTimer = null; }
    saveState.pending = false;
    setSaveStatus('Saving…', false);
    return trackSave(api.saveDraft(attemptId, core.getFiles()))
      .then(function (res) {
        var ok = !!(res && res.success === true);
        setSaveStatus(ok ? 'Saved' : 'Save failed', !ok);
        return res;
      }, function (err) { setSaveStatus('Save failed', true); throw err; });
  };

  // friendlier-challenges: "Use the solution". Inert (renders nothing) for a non-owner viewer or
  // a read-only editor. Its Draft save after loading the model / restored files goes through the
  // flush above, so the debounce is cancelled and the reply's `success` is checked.
  var solution = wireSolution({
    doc: document,
    core: core,
    payload: payload,
    answerKey: answerKey,
    attemptId: attemptId,
    courseCtx: courseCtx,
    isOwner: ctx.isOwner === true,
    scheduleLocked: scheduleLock.enforce,
    flush: function () { return activeFlush ? activeFlush() : Promise.resolve(null); },
    hideResults: function () { hideResults(); },
    checkBtn: checkBtn,
    resultCard: resultSection && resultSection.querySelector('.cr-card'),
    resultFoot: resultFoot
  });

  // Ctrl-S / Cmd-S flushes the pending autosave immediately and saves unconditionally,
  // reporting through this surface's own status element. Cancelling the armed timer is
  // required so it cannot fire afterwards and overwrite the status. DRAFT ONLY — Ctrl-S
  // never triggers Check or Submit. No button to disable here, hence the in-flight flag.
  var ctrlSaving = false;
  registerSaveHandler(function () {
    if (ctrlSaving) { return; }
    if (saveTimer) { clearTimeout(saveTimer); saveTimer = null; }
    saveState.pending = false;
    ctrlSaving = true;
    setSaveStatus('Saving…', false);
    trackSave(api.saveDraft(attemptId, core.getFiles()))
      .then(function () { setSaveStatus('Saved', false); })
      .catch(function (err) { setSaveStatus('Save failed', true); console.error(err); })
      .then(function () { ctrlSaving = false; });
  });

  // --- Persistent, collapsible instructions panel (task 6.1) ------------------
  var toggleBtn = document.getElementById('challenge-instructions-toggle');
  var pane = document.getElementById('challenge-instructions');
  if (toggleBtn && pane) {
    toggleBtn.addEventListener('click', function () {
      var collapsed = pane.classList.toggle('is-collapsed');
      toggleBtn.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
    });
  }

  // ── "What to add" requirements drawer (code-challenge-modify-extend v2) ────────
  // A right-edge overlay on the code pane: a peek TAB that slides a PANEL of the
  // teacher's requirements over the editor. Each row is self-tickable with an
  // optional collapsible hint. Ticks are the student's OWN progress — they persist
  // on the attempt (checklistTicks, debounced POST) and NEVER gate grading (Check
  // still grades the recorded answer key). Rendered only for a modify challenge
  // that carries a non-empty requirements list.
  var DW_CHECK_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>';
  var DW_CHEV_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>';
  // step-by-step-challenges: keeps the step view's "Check my code" in step with the toolbar
  // Check. A no-op unless the step view is wired; called from setChecking and the schedule lock.
  var syncStepCheck = function () {};
  // step-checks: a full Check's running state (set by setChecking) and the step view's hook that
  // clears the "Check this step" result line when a full Check starts (a no-op off the step view).
  var fullCheckRunning = false;
  var onFullCheckStart = function () {};
  // step-result-reach: the step view's re-fit when the drawer opens/closes (a no-op in list mode).
  var onDrawerToggle = function () {};
  (function wireRequirementsDrawer() {
    if (!modifyMode || !requirements.length) { return; }
    var drawer = document.getElementById('challenge-drawer');
    var tab = document.getElementById('challenge-drawer-tab');
    var rowsEl = document.getElementById('challenge-drawer-rows');
    var fillEl = document.getElementById('challenge-drawer-fill');
    var countEl = document.getElementById('challenge-drawer-count');
    var tabCountEl = document.getElementById('challenge-drawer-tabcount');
    var saveNote = document.getElementById('challenge-drawer-save');
    var saveText = document.getElementById('challenge-drawer-save-text');
    // m9-extend-your-game: the four elements that carry the to-do framing.
    var titleEl = document.getElementById('challenge-drawer-title');
    var subEl = document.getElementById('challenge-drawer-sub');
    var subOptionalEl = document.getElementById('challenge-drawer-sub-optional');
    var progressEl = document.getElementById('challenge-drawer-progress');
    var tabLabelEl = document.getElementById('challenge-drawer-tablabel');
    if (!drawer || !tab || !rowsEl) { return; }

    var total = requirements.length;
    // Defensive: checklistTicks are requirement INDICES; a stale index (e.g. after
    // the teacher shortened the list) is simply ignored — indexOf on the saved
    // array yields false for any out-of-range requirement position.
    var ticked = requirements.map(function (_req, i) { return savedTicks.indexOf(i) !== -1; });

    function doneCount() {
      return ticked.reduce(function (n, t) { return n + (t ? 1 : 0); }, 0);
    }
    // m9-extend-your-game: the drawer's one word for an all-optional challenge. It matches the
    // results modal's own vocabulary — the "Optional" tag on a row and "This one is an extra"
    // in its comment — so a student meets the same term in both places.
    var OPTIONAL_TITLE = 'Optional extras';
    // ⚠️ THE ALL-OPTIONAL BRANCH LIVES IN HERE, not in a one-off pass after first paint. This
    // function writes the count, the tab pill and the bar UNCONDITIONALLY, and it runs both at
    // first paint AND on every row tick — so framing applied once outside it would survive
    // until the student's first tick and then be replaced by "0 / 6" with no error anywhere.
    // Re-applying static text on each tick costs nothing at this list size and cannot drift.
    function refreshProgress() {
      var done = doneCount();
      if (allOptional) {
        if (titleEl) { titleEl.textContent = OPTIONAL_TITLE; }
        if (subEl) { subEl.hidden = true; }
        if (subOptionalEl) { subOptionalEl.hidden = false; }
        if (progressEl) { progressEl.hidden = true; }
        if (tabCountEl) { tabCountEl.hidden = true; }
        if (tabLabelEl) { tabLabelEl.textContent = OPTIONAL_TITLE; }
        tab.setAttribute('aria-label', OPTIONAL_TITLE);
        return;
      }
      if (countEl) { countEl.textContent = done + ' / ' + total; }
      if (tabCountEl) { tabCountEl.textContent = done + '/' + total; }
      if (fillEl) { fillEl.style.transform = 'scaleX(' + (total ? done / total : 0) + ')'; }
    }

    // Debounced persistence of the current ticks to the student's OWN attempt.
    // Optimistic: the UI updates immediately; a failed save keeps the local state
    // and surfaces a quiet retry note (the next tick re-attempts the save).
    var saveTimer = null;
    function doSave() {
      var indices = [];
      ticked.forEach(function (t, i) { if (t) { indices.push(i); } });
      return api.saveChecklistTicks(courseCtx, attemptId, indices)
        .then(function () {
          if (saveText) { saveText.textContent = 'Progress saved to your account'; }
        })
        .catch(function (err) {
          if (saveText) { saveText.textContent = 'Couldn’t save — we’ll retry'; }
          if (saveNote) { saveNote.classList.add('is-error'); }
          console.error(err);
        });
    }
    function persistTicks() {
      if (saveTimer) { clearTimeout(saveTimer); }
      if (saveText) { saveText.textContent = 'Saving your progress…'; }
      if (saveNote) { saveNote.classList.remove('is-error'); }
      saveTimer = setTimeout(function () { saveTimer = null; doSave(); }, 600);
    }
    // If the student switches away or closes the tab within the debounce window,
    // flush the pending save immediately (keepalive lets it finish during unload).
    function flushTicks() {
      if (saveTimer) { clearTimeout(saveTimer); saveTimer = null; doSave(); }
    }
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') { flushTicks(); }
    });
    window.addEventListener('pagehide', flushTicks);

    // Optional collapsible hint — its own row BELOW the tick target (a sibling,
    // not nested inside the button), collapsed by default; no control when the
    // teacher left the hint blank. Returns the row, or null when there is no hint.
    // tiered-hints: a row with tier 2 or 3 renders the ladder INSIDE the same hint row in
    // place of the "Hint" toggle/body (the ladder's own "Stuck? Get a hint" is the toggle).
    // A Hint-1-only row keeps the branch below untouched.
    // step-by-step-challenges: shared by the list rows and the step view (one render code).
    function hintRowFor(req, i) {
      if (usesLadder(req)) {
        var ladderRow = document.createElement('div');
        ladderRow.className = 'dw-hint-row';
        ladderRow.appendChild(renderLadder(document, req, {
          renderText   : function (target, text) { target.innerHTML = inlineMarkdown(String(text).trim()); },
          contextLabel : 'requirement ' + (i + 1)
        }));
        return ladderRow;
      }
      else if (req && req.hint && String(req.hint).trim()) {
        var hintRow = document.createElement('div');
        hintRow.className = 'dw-hint-row';
        var toggle = document.createElement('button');
        toggle.type = 'button';
        toggle.className = 'dw-hint-toggle';
        toggle.setAttribute('aria-expanded', 'false');
        toggle.innerHTML = DW_CHEV_SVG + '<span>Show hint</span>';
        var hintBody = document.createElement('div');
        hintBody.className = 'dw-hint-body';
        hintBody.hidden = true;
        hintBody.innerHTML = inlineMarkdown(String(req.hint).trim());
        toggle.addEventListener('click', function () {
          var willOpen = hintBody.hidden;
          hintBody.hidden = !willOpen;
          toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
          toggle.lastChild.textContent = willOpen ? 'Hide hint' : 'Show hint';
        });
        hintRow.appendChild(toggle);
        hintRow.appendChild(hintBody);
        return hintRow;
      }
      return null;
    }

    // step-by-step-challenges (Trello nSM5rv4M): when the teacher turned step mode on and the
    // rule holds (challenge-steps.js stepModeHonoured — re-checked here, all-optional included),
    // the drawer shows ONE requirement at a time inside #challenge-steps and the list is
    // hidden. #challenge-steps is looked up ONLY on this branch, and a missing element falls
    // back to today's list — the off path never touches it.
    var stepsEl = stepModeHonoured(answerKey, allOptional) ? document.getElementById('challenge-steps') : null;
    if (stepsEl) { wireStepView(stepsEl); }

    function wireStepView(host) {
      var resizeEl = document.getElementById('challenge-drawer-resize');
      rowsEl.hidden = true;
      host.hidden = false;
      // The explanatory blurb is gone: the heading, the numbered sub-steps and the buttons say it.
      if (subEl) { subEl.hidden = true; }
      if (tabLabelEl) { tabLabelEl.textContent = 'Steps'; }
      tab.setAttribute('aria-label', 'Steps checklist');
      // Round 5: the title "Step N of M" and the rail carry progress, so the progress block
      // goes (the tab count still shows done/total).
      if (progressEl) { progressEl.hidden = true; }
      if (resizeEl) {
        resizeEl.setAttribute('aria-label', 'Resize the steps panel');
        resizeEl.setAttribute('aria-controls', 'challenge-steps');
      }

      // The step shown is DERIVED from the ticks on load (never stored).
      var current = initialStep(ticked);
      var checkMine = null;
      var railEl = document.createElement('ol');
      railEl.className = 'cs-rail';
      railEl.setAttribute('aria-label', 'Steps');
      // Round 6 layout: #challenge-steps is the ONE scroller, holding the rail (one row of
      // chips), the step body and the foot, in that order. The foot (a ONE-line "Check this
      // step" summary, then the nav) is pinned at the scroller's bottom ONLY while the scroller
      // leaves room above it (fitFoot); the full result lives in the step body.
      var stepEl = document.createElement('div');
      stepEl.className = 'cs-step';
      var footEl = document.createElement('div');
      footEl.className = 'cs-foot';
      // The summary repeats the step-body result for sighted students (the step body's line
      // is the live region), so it is hidden from assistive tech.
      var footSummaryEl = document.createElement('p');
      footSummaryEl.className = 'cs-stepcheck-summary';
      footSummaryEl.setAttribute('aria-hidden', 'true');
      footEl.appendChild(footSummaryEl);
      var navEl = document.createElement('div');
      navEl.className = 'cs-nav';
      footEl.appendChild(navEl);
      host.appendChild(railEl);
      host.appendChild(stepEl);
      host.appendChild(footEl);

      // The pinned foot may never hide what it covers: while it is pinned the scroller's
      // scroll-padding-bottom is its height (focus and reveal scrolling stop above it), and when
      // the scroller is too short to leave FOOT_MIN_ROOM_PX above it the foot is unpinned and
      // simply follows the step (nothing is covered at all). Re-measured on every visit, every
      // result and every resize of the scroller.
      // step-result-reach: while a step-check result shows and the scroller is too short to hold
      // result + foot, the open drawer grows UPWARD over the banner (--cs-reach on the drawer).
      // currentReach is what is applied now; the pin test measures the BASE height (clientHeight
      // minus the reach) so growing never flips the foot to pinned and never feeds back.
      var currentReach = 0;
      function rectOf(el) {
        return (el && typeof el.getBoundingClientRect === 'function') ? el.getBoundingClientRect() : null;
      }
      // grownBy = how much the scroller ACTUALLY grew when the reach was applied (measured, so a
      // clientHeight that does not track the reach can never shrink the base height and make the
      // need escalate on every re-fit).
      var grownBy = 0;
      function baseHeight() { return (host.clientHeight || 0) - grownBy; }
      function pinnedAt(baseH, footH) { return !(baseH && baseH - footH < FOOT_MIN_ROOM_PX); }
      function setReach(r) {
        if (r === currentReach) { return false; }
        var st = drawer.style;
        if (r <= 0) {
          if (!st || typeof st.removeProperty !== 'function') { return false; }
          st.removeProperty('--cs-reach');
          currentReach = 0;
          grownBy = 0;
          return true;
        }
        if (currentReach !== 0 && Math.abs(r - currentReach) <= 2) { return false; }
        if (!st || typeof st.setProperty !== 'function') { return false; }
        var before = host.clientHeight || 0;
        st.setProperty('--cs-reach', r + 'px');
        currentReach = r;
        grownBy = Math.max(0, grownBy + (host.clientHeight || 0) - before);
        return true;
      }
      function computeReach() {
        if (!drawer.classList || !drawer.classList.contains('is-open')) { return 0; }
        if (!stepResultEl || !stepResultEl.textContent) { return 0; }
        var banner = document.getElementById('instructions-banner');
        var pane = drawer.parentElement || drawer.parentNode;
        var rr = rectOf(stepResultEl);
        var fr = rectOf(footEl);
        var pr = rectOf(pane);
        var br = rectOf(banner);
        if (!rr || !fr || !pr || !br) { return 0; }
        var baseH = baseHeight();
        // The reach applies only while the foot is unpinned: a pinned (sticky) foot's rect moves
        // with the scroll, so the span below would not be layout-based.
        if (currentReach === 0 && pinnedAt(baseH, footEl.offsetHeight || 0)) { return 0; }
        var span = fr.bottom - rr.top;
        var need = Math.max(0, span + DRAWER_REACH_SLACK_PX - baseH);
        if (!need) { return 0; }
        var rowBottom = null;
        if (typeof banner.querySelector === 'function') {
          ['.instructions-banner-toggle', '.pcra-btn-banner'].forEach(function (sel) {
            var r = rectOf(banner.querySelector(sel));
            if (r && (rowBottom === null || r.bottom > rowBottom)) { rowBottom = r.bottom; }
          });
        }
        if (rowBottom === null) { rowBottom = br.top + DRAWER_BANNER_KEEP_PX; }
        var floorY = Math.max(0, rowBottom + DRAWER_REACH_SLACK_PX);
        var room = Math.max(0, pr.top - floorY);
        return Math.ceil(Math.min(need, room));
      }
      function fitReach() { return setReach(computeReach()); }
      function applyPin() {
        var footH = footEl.offsetHeight || 0;
        var unpin = currentReach > 0 || !pinnedAt(baseHeight(), footH);
        if (host.classList) { host.classList.toggle('is-foot-unpinned', unpin); }
        if (host.style && typeof host.style.setProperty === 'function') {
          host.style.setProperty('--cs-foot-h', (unpin ? 0 : footH) + 'px');
        }
        return !unpin;
      }
      function fitFoot() {
        var pinned = applyPin();
        if (fitReach()) { pinned = applyPin(); }
        return pinned;
      }
      // Scroll `el` (inside the scroller) fully into the part the pinned foot does not cover.
      function revealInSteps(el) {
        if (!el || typeof el.getBoundingClientRect !== 'function' ||
            typeof host.getBoundingClientRect !== 'function') { return; }
        var pinned = fitFoot();
        var hr = host.getBoundingClientRect();
        var er = el.getBoundingClientRect();
        // The step result (unpinned foot): bring the foot's bottom to the scroller's bottom when
        // result + foot fit the (grown) scroller, else fall through to result-top-first.
        var fr = (el === stepResultEl && !pinned) ? rectOf(footEl) : null;
        if (fr && fr.bottom - er.top <= (host.clientHeight || 0)) {
          if (fr.bottom > hr.bottom) { host.scrollTop += fr.bottom - hr.bottom; }
          else if (er.top < hr.top) { host.scrollTop -= hr.top - er.top; }
          return;
        }
        var bottom = hr.bottom - (pinned ? (footEl.offsetHeight || 0) : 0);
        if (er.bottom > bottom) { host.scrollTop += Math.min(er.bottom - bottom, er.top - hr.top); }
        else if (er.top < hr.top) { host.scrollTop -= hr.top - er.top; }
      }
      if (typeof ResizeObserver === 'function') {
        var ro = new ResizeObserver(function () { fitFoot(); });
        ro.observe(host);
        var bannerEl = document.getElementById('instructions-banner');
        if (bannerEl) { ro.observe(bannerEl); }
      }
      onDrawerToggle = function () { fitFoot(); };

      // "Check my code" mirrors the toolbar Check: disabled while it is (a running Check) and
      // under an enforced schedule lock — read directly, because this runs before the lock
      // wiring disables the toolbar button.
      function checkDisabled() {
        return !checkBtn || !!checkBtn.disabled || scheduleLock.enforce === true;
      }
      // solution-pieces-merge (Trello daAruJ5U): the reached step's solution pieces, mounted
      // after the hint row on each visit. Built only for an active (owner, editable) solution
      // controller with honoured pieces; otherwise the step view is exactly as before.
      var pieceSteps = (solution.active && Array.isArray(answerKey.pieceSteps)) ? answerKey.pieceSteps : [];
      var pieces = pieceSteps.length ? createPiecesPanel({
        doc: document,
        core: core,
        solution: solution,
        pieceSteps: pieceSteps,
        courseCtx: courseCtx,
        attemptId: attemptId
      }) : null;
      // "Reached" = index <= the first unticked step (all ticked = every step), read LIVE.
      function reached(i) {
        var first = ticked.indexOf(false);
        return first < 0 || i <= first;
      }
      if (pieces) {
        // The open step re-renders in place when a failed Check crosses the threshold, and its
        // file gate is re-checked when the student switches tabs (Ace `changeSession`).
        solution.onOffered(function () { pieces.refresh(true); });
        var aceEditor = core.getEditor && core.getEditor();
        if (aceEditor && typeof aceEditor.on === 'function') {
          aceEditor.on('changeSession', function () { pieces.refresh(false); });
        }
      }

      // step-checks: "Check this step" — only for the work's owner (the SAME flag that makes
      // the chips and "Use the solution" inert; a teacher on a student's copy gets none), and
      // only on a step with a row tagged exactly that step. Its disabled state is computed from
      // the full Check's own running state, never from checkBtn.disabled (which it sets).
      var stepCheckOn = !chipsDisabled;
      var stepCheckBtn = null;
      var stepResultEl = null;
      function stepCheckShown(i) { return stepCheckOn && stepHasCheck(i + 1); }
      function stepCheckDisabled() {
        return stepCheckBusy || fullCheckRunning || scheduleLock.enforce === true;
      }
      function paintStepCheck() {
        if (!stepCheckBtn) { return; }
        stepCheckBtn.disabled = stepCheckDisabled();
        stepCheckBtn.textContent = stepCheckBusy ? 'Checking…' : 'Check this step';
        if (stepCheckBusy) { stepCheckBtn.setAttribute('aria-busy', 'true'); }
        else if (stepCheckBtn.removeAttribute) { stepCheckBtn.removeAttribute('aria-busy'); }
        else { stepCheckBtn.setAttribute('aria-busy', 'false'); }
      }
      // The result: every line in the step body (the live region, scrolled into view), and ONE
      // summary line in the pinned foot. `null` clears both.
      function setStepResult(msg) {
        var has = !!(msg && Array.isArray(msg.lines) && msg.lines.length);
        footSummaryEl.textContent = has ? stepCheckSummary(msg) : '';
        footSummaryEl.classList.remove('is-pass');
        footSummaryEl.classList.remove('is-fail');
        if (has) { footSummaryEl.classList.add(msg.tone === 'pass' ? 'is-pass' : 'is-fail'); }
        if (stepResultEl) {
          stepResultEl.textContent = '';
          stepResultEl.classList.remove('is-pass');
          stepResultEl.classList.remove('is-fail');
          if (has) {
            stepResultEl.classList.add(msg.tone === 'pass' ? 'is-pass' : 'is-fail');
            msg.lines.forEach(function (line) {
              var span = document.createElement('span');
              span.className = 'cs-stepcheck-line';
              span.textContent = String(line);
              stepResultEl.appendChild(span);
            });
          }
        }
        fitFoot();
        if (has) { revealInSteps(stepResultEl); }
      }
      onFullCheckStart = function () { setStepResult(null); };
      syncStepCheck = function () {
        if (checkMine) { checkMine.disabled = checkDisabled(); }
        paintStepCheck();
        // A running Check / the schedule lock disables "Merge at cursor" too.
        if (pieces) { pieces.refresh(false); }
      };
      function onStepCheck() {
        if (!stepCheckBtn || stepCheckDisabled()) { return; }
        var checked = current;
        var btn = stepCheckBtn;
        // Disabling the focused button drops focus to <body> in a browser; give it back after.
        var hadFocus = document.activeElement === btn;
        setStepResult(null);
        runStepCheck(checked + 1).then(function (msg) {
          if (hadFocus && stepCheckBtn === btn && current === checked && typeof btn.focus === 'function') {
            btn.focus();
          }
          // A pass marks the CHECKED step done (the student may have moved on mid-check); it
          // never advances and never re-renders the nav, so focus stays on the button.
          if (msg && msg.tone === 'pass') {
            ticked[checked] = true;
            refreshProgress();
            renderRail();
            persistTicks();
          }
          if (current === checked) { setStepResult(msg); }
        });
      }

      function goTo(i) {
        if (i < 0 || i >= requirements.length) { return; }
        current = i;
        renderVisit(true);
      }

      // One small dot per step — done, here, or still to do. No numbers, ticks or locks: the
      // heading says which step this is, and any dot can be pressed to look at that step.
      function renderRail() {
        railEl.innerHTML = '';
        var states = stepStates(ticked, current);
        requirements.forEach(function (req, i) {
          var st = states[i];
          var li = document.createElement('li');
          var text = railText(req && req.label);
          var row = document.createElement('button');
          row.type = 'button';
          row.addEventListener('click', function () { goTo(i); });
          row.className = 'cs-rail-chip is-' + st;
          if (st === 'current') { row.setAttribute('aria-current', 'step'); }
          row.setAttribute('title', text);
          row.setAttribute('aria-label', 'Step ' + (i + 1) + ': ' + text +
            ' (' + (st === 'open' ? 'not done' : st) + ')');
          var face = document.createElement('span');
          face.className = 'cs-rail-face';
          face.setAttribute('aria-hidden', 'true');
          row.appendChild(face);
          li.appendChild(row);
          railEl.appendChild(li);
        });
      }

      function renderNav() {
        var last = isLastStep(ticked, current);
        navEl.innerHTML = '';
        var back = document.createElement('button');
        back.type = 'button';
        back.className = 'cs-btn cs-btn-nav';
        back.textContent = 'Back';
        back.disabled = current === 0;
        back.addEventListener('click', function () { if (current > 0) { goTo(current - 1); } });
        navEl.appendChild(back);
        stepCheckBtn = null;
        if (stepCheckShown(current)) {
          stepCheckBtn = document.createElement('button');
          stepCheckBtn.type = 'button';
          stepCheckBtn.className = 'cs-btn cs-btn-stepcheck';
          stepCheckBtn.addEventListener('click', onStepCheck);
          paintStepCheck();
          navEl.appendChild(stepCheckBtn);
        }
        if (last) {
          // Completion goes through the existing Check path; no tick needed.
          var check = document.createElement('button');
          check.type = 'button';
          check.className = 'cs-btn cs-btn-check';
          check.textContent = 'Check my code';
          check.disabled = checkDisabled();
          check.addEventListener('click', function () {
            if (checkDisabled()) { return; }
            runCheck();
          });
          navEl.appendChild(check);
          checkMine = check;
        } else {
          var next = document.createElement('button');
          next.type = 'button';
          next.className = 'cs-btn cs-btn-next';
          next.textContent = 'Next step';
          next.disabled = !canGoNext(ticked, current);
          next.addEventListener('click', function () {
            if (!canGoNext(ticked, current)) { return; }
            // Moving on is how a step becomes "done": the same ticks[] and debounced save as
            // the list, with no checkbox for the student to hunt for.
            if (!ticked[current]) {
              ticked[current] = true;
              refreshProgress();
              persistTicks();
            }
            goTo(current + 1);
          });
          navEl.appendChild(next);
          checkMine = null;
        }
      }

      // The step body is rebuilt on every VISIT (hints start closed again, like the list's).
      function renderVisit(moveFocus) {
        if (titleEl) { titleEl.textContent = 'Step ' + (current + 1) + ' of ' + total; }
        var req = requirements[current];
        stepEl.innerHTML = '';
        host.scrollTop = 0;
        var heading = document.createElement('h4');
        heading.className = 'cs-step-label';
        heading.setAttribute('tabindex', '-1');
        heading.innerHTML = chipifyHtml(inlineMarkdown(String(req.label).trim()), { disabled: chipsDisabled });
        stepEl.appendChild(heading);
        // Two or more lines = a numbered list of sub-steps; one line stays the sentence it was.
        var parts = subSteps(req && req.text);
        if (parts.length > 1) {
          var list = document.createElement('ol');
          list.className = 'cs-list';
          parts.forEach(function (part) {
            var item = document.createElement('li');
            item.innerHTML = chipifyHtml(inlineMarkdown(part), { disabled: chipsDisabled });
            list.appendChild(item);
          });
          stepEl.appendChild(list);
        } else if (parts.length === 1) {
          var desc = document.createElement('div');
          desc.className = 'dw-desc';
          desc.innerHTML = chipifyHtml(inlineMarkdown(parts[0]), { disabled: chipsDisabled });
          stepEl.appendChild(desc);
        }
        // step-checks: the "Check this step" result — a polite status region in the step body,
        // right under the description, scrolled into view clear of the pinned foot when it fills
        // (the foot only repeats it as one summary line). Rebuilt (so cleared) on every visit,
        // never persisted.
        stepResultEl = null;
        footSummaryEl.textContent = '';
        if (stepCheckShown(current)) {
          stepResultEl = document.createElement('p');
          stepResultEl.className = 'cs-stepcheck-result';
          stepResultEl.setAttribute('role', 'status');
          stepEl.appendChild(stepResultEl);
        }
        var hintRowEl = hintRowFor(req, current);
        if (hintRowEl) { stepEl.appendChild(hintRowEl); }
        if (pieces) { pieces.mount(stepEl, { step: current + 1, reached: reached(current) }); }

        renderRail();
        renderNav();
        fitFoot();
        if (moveFocus && typeof heading.focus === 'function') { heading.focus(); }
      }

      renderVisit(false);
    }

    requirements.forEach(function (req, i) {
      // step-by-step-challenges: the step view replaces the list; nothing is built here.
      if (stepsEl) { return; }
      var li = document.createElement('li');
      li.className = 'dw-check' + (ticked[i] ? ' is-done' : '');
      var reqLabel = (req && typeof req.label === 'string') ? req.label.trim() : '';

      // friendlier-challenges (decisions Round 2): a LABELLED row is restructured so no button
      // nests in a button — the tick is its own small button (the same box look), and the
      // heading + description are SIBLINGS outside it, so their insert chips work. A row with
      // no label falls through to the original markup below, byte for byte.
      if (reqLabel) {
        li.className += ' has-label';
        var row = document.createElement('div');
        row.className = 'dw-check-main is-labelled';
        var tick = document.createElement('button');
        tick.type = 'button';
        tick.className = 'dw-tick';
        tick.setAttribute('aria-pressed', ticked[i] ? 'true' : 'false');
        tick.setAttribute('aria-label', 'Mark “' + reqLabel.replace(/[`*]/g, '') + '” done');
        var tickBox = document.createElement('span');
        tickBox.className = 'dw-box';
        tickBox.setAttribute('aria-hidden', 'true');
        tickBox.innerHTML = DW_CHECK_SVG;
        tick.appendChild(tickBox);
        row.appendChild(tick);
        var ltxt = document.createElement('div');
        ltxt.className = 'dw-txt';
        var heading = document.createElement('div');
        heading.className = 'dw-name';
        heading.innerHTML = chipifyHtml(inlineMarkdown(reqLabel), { disabled: chipsDisabled });
        ltxt.appendChild(heading);
        var descText = (req && req.text) ? String(req.text).trim() : '';
        if (descText) {
          var desc = document.createElement('div');
          desc.className = 'dw-desc';
          desc.innerHTML = chipifyHtml(inlineMarkdown(descText), { disabled: chipsDisabled });
          ltxt.appendChild(desc);
        }
        row.appendChild(ltxt);
        li.appendChild(row);
        tick.addEventListener('click', function () {
          ticked[i] = !ticked[i];
          li.classList.toggle('is-done', ticked[i]);
          tick.setAttribute('aria-pressed', ticked[i] ? 'true' : 'false');
          refreshProgress();
          persistTicks();
        });
      } else {

        // The tick target — a real button (box + text) so it is keyboard-operable.
        var main = document.createElement('button');
        main.type = 'button';
        main.className = 'dw-check-main';
        main.setAttribute('aria-pressed', ticked[i] ? 'true' : 'false');

        var box = document.createElement('span');
        box.className = 'dw-box';
        box.setAttribute('aria-hidden', 'true');
        box.innerHTML = DW_CHECK_SVG;
        main.appendChild(box);

        var txt = document.createElement('span');
        txt.className = 'dw-txt';
        var name = document.createElement('span');
        name.className = 'dw-name';
        // Requirement text is teacher-authored markdown (13 inline-code spans across the Year
        // 10 pack, no bold) and used to render its backticks literally — "call it `alien`".
        // inlineMarkdown escapes first and whitelists second, so this stays safe against the
        // untrusted string it has always been. See inline-markdown.js.
        name.innerHTML = (req && req.text && String(req.text).trim())
          ? inlineMarkdown(String(req.text).trim()) : ('Requirement ' + (i + 1));
        txt.appendChild(name);
        main.appendChild(txt);
        li.appendChild(main);

        main.addEventListener('click', function () {
          ticked[i] = !ticked[i];
          li.classList.toggle('is-done', ticked[i]);
          main.setAttribute('aria-pressed', ticked[i] ? 'true' : 'false');
          refreshProgress();
          persistTicks();
        });
      }

      var hintRowEl = hintRowFor(req, i);
      if (hintRowEl) { li.appendChild(hintRowEl); }

      rowsEl.appendChild(li);
    });

    function setOpen(open) {
      drawer.classList.toggle('is-open', open);
      tab.setAttribute('aria-expanded', open ? 'true' : 'false');
      onDrawerToggle();
    }
    tab.addEventListener('click', function () {
      setOpen(!drawer.classList.contains('is-open'));
    });

    // what-to-add-drawer-arrow-and-dismiss (Trello cqn6yxJR): a press inside the code
    // editor closes an already-open drawer. Document-level CAPTURE phase so Ace's own
    // mouse handlers cannot swallow it first; never prevented/stopped, so the press
    // still places the caret. Close-only (never opens) — no toggle.
    // Step mode is the exception: the steps are what the student reads while typing, so only the
    // peek tab opens and closes that drawer.
    if (!stepsEl) {
      document.addEventListener('pointerdown', function (e) {
        var t = e && e.target;
        if (!(t && typeof t.closest === 'function' && t.closest('.editor-host'))) { return; }
        if (drawer.classList.contains('is-open')) { setOpen(false); }
      }, true);
    }

    refreshProgress();
    drawer.hidden = false;
    // challenge-drawer-right (A2b): the peek tab sits on the pane's right edge, so inset the
    // editor by the tab's width (clientside-base.html) — only when this challenge really has a
    // drawer, so a challenge with none keeps the editor full width.
    document.body.classList.add('has-challenge-drawer');
    // Open on first paint so the student sees the task list; the tab tucks it away.
    setOpen(true);
  })();

  // ── Check-results modal ───────────────────────────────────────────────────
  // Line SVG icons (Lucide-style), inlined so the modal is self-contained.
  var ICON = {
    check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>',
    x: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>',
    hint: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 17h.01M12 13a2 2 0 1 0-2-2"/></svg>',
    // code-challenge-dotted-path-checkpoints: a neutral dash-in-circle for a
    // skipped/inconclusive check (never a check or an x).
    skip: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/></svg>',
    // advisory-checkpoints (B1): an OPTIONAL extension the student has not built
    // yet. A plus reads as "something extra you could add" and is distinct from
    // both ICON.hint (question mark) and ICON.skip (dash) — the three states must
    // stay tellable apart.
    optional: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14M5 12h14"/></svg>',
    trophy: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6M18 9h1.5a2.5 2.5 0 0 0 0-5H18M4 22h16M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22M18 2H6v7a6 6 0 0 0 12 0V2Z"/></svg>',
    target: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1.4" fill="currentColor"/></svg>',
    alert: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0zM12 9v4M12 17h.01"/></svg>'
  };

  function setTone(tone) {
    if (!resultSection) { return; }
    resultSection.classList.remove('is-success', 'is-fail', 'is-error', 'is-checking');
    resultSection.classList.add('is-' + tone);
  }
  function setProgress(frac) {
    if (resultProgress) { resultProgress.style.width = Math.round(Math.max(0, Math.min(1, frac)) * 100) + '%'; }
  }
  function openModal() { if (resultSection) { resultSection.hidden = false; } }
  function hideResults() { if (resultSection) { resultSection.hidden = true; } }

  var STDIN_TAG_NAME = { expected: 'Expected test', boundary: 'Edge case', invalid: 'Bad input test' };
  var STDIN_TAG_PASS = {
    expected: 'Your program gave the right result for a normal input.',
    boundary: 'Your program handled this tricky value correctly.',
    invalid: 'Your program handled this bad input without crashing.'
  };

  // A short, friendly name for a single-row result.
  function rowName(r) {
    if (r.label) { return r.label; }
    switch (r.kind) {
      case 'expected-output': return 'Console output';
      case 'runs-clean': return 'Runs without crashing';
      case 'raises-on': return 'Rejects bad input';
      case 'handles-gracefully': return 'Handles bad input';
      case 'function-io': return 'Function' + (r.functionName ? ' ' + r.functionName + '()' : '');
      case 'behavioral': return 'Behaviour';
      case 'structural': return 'Structure';
      default: return isTemporalKind(r.kind) ? (r.variable || 'Motion') : (r.variable || 'Value');
    }
  }
  // A detailed, plain-language comment. Failures (and some passes) already carry a
  // friendly `diff`; otherwise fall back to a per-kind pass sentence.
  function rowComment(r) {
    if (r.diff) { return r.diff; }
    if (r.passed) {
      switch (r.kind) {
        case 'expected-output': return 'Your program printed exactly the output we expected.';
        case 'runs-clean': return 'Your program ran all the way through and finished without any errors.';
        case 'raises-on': return 'Your program stopped with the error we expected.';
        case 'handles-gracefully': return 'Your program dealt with the bad input and carried on instead of crashing.';
        case 'function-io': return 'Your function returned the right answer for every test we tried.';
        case 'behavioral': return 'Your program behaved the way we expected.';
        case 'flag-becomes': return 'Your game reached the state we were looking for.';
        case 'state-sequence': return 'Your game moved through the states in the right order.';
        // game-checkpoint-false-negative: `tResult` forces `diff: ''` on a pass,
        // so without this case a `steps-down` row falls to the default below and
        // tells a student their SCORE's "motion" matched — which is not what a
        // score is. `rowName` needs no case: it already falls back to the row's
        // label (M4.42 has one) and then to the variable name.
        case 'steps-down': return 'This number did go down during play, just like we hoped.';
        default: return isTemporalKind(r.kind) ? 'The motion in your program matched what we expected.' : 'This value came out right.';
      }
    }
    return 'This check didn’t match.';
  }

  // advisory-checkpoints (D2): a result the grader marked ADVISORY — an optional
  // extension that never gates the verdict. `structural` and `skipped` return
  // earlier in describeResult, so this predicate only ever sees genuine optional
  // extensions.
  function isAdvisory(r) { return !!r && r.hintOnly === true; }

  // advisory-checkpoints (B5): an advisory row gets its OWN copy on both outcomes.
  // A failed one must never surface the evaluator's `diff` — every temporal fail
  // `diff` is a failure sentence naming the threshold ("…reached 120, but the
  // target is >= 400"), which reads as "you got this wrong". A passed one must not
  // fall through to rowComment's generic "The motion in your program matched what
  // we expected.", which is substantively wrong under a row named "Boss fight".
  // The comment names the checkpoint's own `label` so a menu of six extensions
  // reads as six different things rather than one sentence repeated six times.
  function advisoryComment(r) {
    var label = r && r.label ? String(r.label) : '';
    if (r && r.passed) {
      return label
        ? 'Nice extra — you built ' + label + '. This one was optional.'
        : 'Nice extra — you built this one, and it was optional.';
    }
    return label
      ? 'Not there yet: ' + label + '. This one is an extra — your check doesn’t depend on it.'
      : 'Not there yet. This one is an extra — your check doesn’t depend on it.';
  }

  // Map ONE grading result to display rows { status:'pass'|'fail'|'hint'|'skip'|
  // 'advisory', name, comment, input, group, advisory }. A stdin-transcript result
  // expands into one row per test category, grouped under "Input tests".
  function describeResult(r, cp) {
    if (r.kind === 'structural') {
      return [{ status: 'hint', name: rowName(r), comment: r.diff || 'A friendly hint — this never fails your check.' }];
    }
    // code-challenge-dotted-path-checkpoints: a skipped (naming-variety fallback
    // inconclusive) result renders NEUTRAL — never the default green/red — and is
    // excluded from the pass tally below. Without this it would fall through to the
    // default `passed ? 'pass' : 'fail'` and show a false green checkmark.
    if (r.skipped) {
      return [{ status: 'skip', name: rowName(r),
        comment: r.diff || "We couldn't automatically check this one — it's worth a manual look.",
        advisory: isAdvisory(r) || undefined }];
    }
    // advisory-checkpoints (F1): the stdin-transcript expansion KEEPS its position
    // and still runs — a blanket advisory return placed above it would silently
    // collapse a multi-case transcript into one row. Instead the expanded rows
    // INHERIT the advisory marker, and a non-passing expanded row renders
    // 'advisory' rather than 'fail'. Without inheritance overallPassed would
    // exclude the checkpoint while the tally still counted its rows — "you
    // passed!" above "2 of 3 checks passed".
    if (r.kind === 'stdin-transcript' && Array.isArray(r.tagResults) && r.tagResults.length) {
      var optional = isAdvisory(r);
      // friendlier-challenges: the "what it looked for" sentence goes on the FIRST failing
      // input-test row only — the same sentence on every row of one checkpoint is noise.
      var lookedForGiven = false;
      return r.tagResults.map(function (t) {
        var looked;
        if (!t.passed && !optional && !lookedForGiven) { looked = lookedFor(cp); lookedForGiven = true; }
        return {
          lookedFor: looked,
          status: t.passed ? 'pass' : (optional ? 'advisory' : 'fail'),
          name: STDIN_TAG_NAME[t.tag] || 'Input test',
          // One entry per case: show the first case's inputs only, so several cases' inputs
          // are never run together into one unreadable line.
          input: (t.inputs && t.inputs.length)
            ? t.inputs[0] + (t.inputs.length > 1 ? ' (and ' + (t.inputs.length - 1) + ' more ' + (t.inputs.length === 2 ? 'case' : 'cases') + ')' : '')
            : '',
          comment: t.passed
            ? STDIN_TAG_PASS[t.tag]
            : (optional
              ? 'Not there yet. This input test is an extra — your check doesn’t depend on it.'
              : (t.diff || 'This case didn’t match.')),
          group: 'Input tests',
          advisory: optional || undefined
        };
      });
    }
    // advisory-checkpoints (D2): status by OUTCOME, exclusion by an INDEPENDENT
    // marker. A built extension is a normal green pass row; an unbuilt one is
    // neutral 'advisory'. Either way the row carries `advisory: true`, which is
    // what showResults' tally filters on — a status-only filter cannot express
    // "green, but not counted".
    if (isAdvisory(r)) {
      return [{
        status: r.passed ? 'pass' : 'advisory',
        name: rowName(r),
        comment: advisoryComment(r),
        input: '',
        advisory: true,
        evidence: r.kind === 'function-io' ? functionEvidence(r) : null
      }];
    }
    var row = { status: r.passed ? 'pass' : 'fail', name: rowName(r), comment: rowComment(r), input: '',
      evidence: r.kind === 'function-io' ? functionEvidence(r) : null };
    // friendlier-challenges (decisions §2 + Round 2/4): a FAILING, non-advisory row says in one
    // sentence what the check looked for. A failed expected-output row also shows the near-miss
    // label and the Expected / Your program printed columns IN PLACE OF its `diff` text — the
    // display only: the POSTed row (buildResultRow) still carries `diff` unchanged.
    if (!r.passed) {
      row.lookedFor = lookedFor(cp);
      if (r.kind === 'expected-output' && typeof r.expected === 'string' && typeof r.got === 'string') {
        var ld = lineDiff(r.expected, r.got);
        row.diffView = { diff: ld, nearMiss: firstNearMiss(ld) };
      }
    }
    return [row];
  }

  // friendlier-challenges: the checkpoint DEFINITION behind a graded result, for lookedFor.
  // compareAll maps checkpoints one-to-one and in order, so the index pairs them; the result's
  // own fields are the fallback (and fill in anything the definition lacks).
  function checkpointFor(results, idx) {
    var r = results[idx] || {};
    var cp = (results.length === checkpoints.length) ? checkpoints[idx] : null;
    return cp ? Object.assign({}, r, cp) : r;
  }

  // Fold results into grouped sections [{ label, rows }] — consecutive rows sharing
  // a `group` collect under one labelled section; ungrouped rows stand alone.
  function resultsToSections(results) {
    var flat = [];
    (results || []).forEach(function (r, idx) {
      describeResult(r, checkpointFor(results, idx)).forEach(function (row) { flat.push(row); });
    });
    var sections = [];
    var current = null;
    flat.forEach(function (row) {
      var g = row.group || null;
      if (!current || current.label !== g) { current = { label: g, rows: [] }; sections.push(current); }
      current.rows.push(row);
    });
    return sections;
  }

  function renderRow(row) {
    var li = document.createElement('li');
    li.className = 'cr-row ' + (row.status === 'pass' ? 'is-pass' : row.status === 'fail' ? 'is-fail'
      : row.status === 'skip' ? 'is-skip' : row.status === 'advisory' ? 'is-advisory' : 'is-hint');
    var chip = document.createElement('span');
    chip.className = 'cr-chip';
    chip.innerHTML = row.status === 'pass' ? ICON.check : row.status === 'fail' ? ICON.x
      : row.status === 'skip' ? ICON.skip : row.status === 'advisory' ? ICON.optional : ICON.hint;
    // advisory-checkpoints (B2): the chip is raw decorative SVG with no role and
    // no accessible name — hide it from assistive tech rather than letting it be
    // announced as an unlabelled graphic. Row status is carried by text instead.
    chip.setAttribute('aria-hidden', 'true');
    li.appendChild(chip);
    var main = document.createElement('div');
    var name = document.createElement('div');
    name.className = 'cr-name';
    name.appendChild(document.createTextNode(row.name));
    // advisory-checkpoints (B2/D4): "Optional" is REAL TEXT in the DOM, on a pass
    // and on a fail alike. Without it a student who builds three extensions sees
    // four green ticks under "All 1 check passed" — the header contradicting the
    // list — and a colour-blind or screen-reader student cannot tell "optional,
    // not built" from "failed" at all, because status is otherwise colour + icon.
    if (row.advisory) {
      var tag = document.createElement('span');
      tag.className = 'cr-tag';
      tag.textContent = 'Optional';
      name.appendChild(tag);
    }
    if (row.input) {
      var chipIn = document.createElement('span');
      chipIn.className = 'cr-input';
      chipIn.textContent = String(row.input).replace(/`/g, '');
      name.appendChild(chipIn);
    }
    main.appendChild(name);
    // friendlier-challenges: what the check looked for (inline markdown — the names in it are
    // teacher-authored; inlineMarkdown escapes first). No expected value is ever in it.
    if (row.lookedFor) {
      var looked = document.createElement('p');
      looked.className = 'cr-looked';
      looked.innerHTML = inlineMarkdown(row.lookedFor);
      main.appendChild(looked);
    }
    if (row.diffView) {
      if (row.diffView.nearMiss) {
        var near = document.createElement('p');
        near.className = 'cr-nearmiss';
        near.textContent = row.diffView.nearMiss;
        main.appendChild(near);
      }
      main.appendChild(renderOutputDiff(row.diffView.diff));
    } else {
      var comment = document.createElement('p');
      comment.className = 'cr-comment';
      comment.textContent = row.comment;
      main.appendChild(comment);
    }
    if (row.evidence && row.evidence.lines && row.evidence.lines.length) {
      main.appendChild(renderEvidence(row.evidence));
    }
    li.appendChild(main);
    return li;
  }

  // friendlier-challenges: the Expected / Your program printed columns of a failed
  // expected-output row. One list item per aligned line in BOTH columns (so the two line up
  // side by side), the differing lines highlighted and the differing span in a <mark>. Every
  // string goes in through textContent — the right-hand column is student output. Each
  // changed / missing EXPECTED line is an insert chip (when it is short enough to be one).
  // Long output shows a 40-line window from 5 lines before the first difference.
  function appendLineText(target, text, span) {
    if (text === '') {
      var empty = document.createElement('span');
      empty.className = 'cr-diff-empty';
      empty.textContent = '(empty line)';
      target.appendChild(empty);
      return;
    }
    if (span && span[1] > span[0]) {
      if (span[0] > 0) { target.appendChild(document.createTextNode(text.slice(0, span[0]))); }
      var mark = document.createElement('mark');
      mark.textContent = text.slice(span[0], span[1]);
      target.appendChild(mark);
      if (span[1] < text.length) { target.appendChild(document.createTextNode(text.slice(span[1]))); }
      return;
    }
    target.appendChild(document.createTextNode(text));
  }

  function diffLine(row, side) {
    var li = document.createElement('li');
    li.className = 'cr-diff-line is-' + row.status;
    var text = side === 'expected' ? row.expected : row.got;
    if (text == null) {
      li.className += ' is-blank';
      var none = document.createElement('span');
      none.className = 'cr-diff-none';
      none.textContent = side === 'expected' ? '(not expected)' : '(nothing printed)';
      li.appendChild(none);
      return li;
    }
    var span = row.status === 'changed' ? (side === 'expected' ? row.expSpan : row.gotSpan) : null;
    var holder = li;
    if (side === 'expected' && (row.status === 'changed' || row.status === 'missing')) {
      var chip = buildChip(document, text, { disabled: chipsDisabled });
      if (chip) { li.appendChild(chip); holder = chip; }
    }
    appendLineText(holder, text, span);
    return li;
  }

  function renderOutputDiff(diff) {
    var win = diffWindow(diff.rows, diff.firstDiff);
    var shown = diff.rows.slice(win.start, win.end);
    var box = document.createElement('div');
    box.className = 'cr-diff';
    [['expected', 'Expected'], ['got', 'Your program printed']].forEach(function (col) {
      var column = document.createElement('div');
      column.className = 'cr-diff-col cr-diff-' + col[0];
      var head = document.createElement('p');
      head.className = 'cr-diff-head';
      head.textContent = col[1];
      column.appendChild(head);
      var ol = document.createElement('ol');
      ol.className = 'cr-diff-lines';
      shown.forEach(function (row) { ol.appendChild(diffLine(row, col[0])); });
      column.appendChild(ol);
      box.appendChild(column);
    });
    if (win.before || win.after) {
      var more = document.createElement('p');
      more.className = 'cr-diff-more';
      var bits = [];
      if (win.before) { bits.push('… ' + win.before + ' earlier ' + (win.before === 1 ? 'line' : 'lines')); }
      if (win.after) { bits.push('… ' + win.after + ' more ' + (win.after === 1 ? 'line' : 'lines')); }
      more.textContent = bits.join('  ');
      box.appendChild(more);
    }
    return box;
  }

  // return-evidence (Trello DyXzvVbP): what the checker actually saw, case by case — the
  // positive evidence a return-only function otherwise never gives (its console is empty by
  // design). EVERY string goes in through textContent: a returned value is student data.
  function renderEvidence(ev) {
    var box = document.createElement('div');
    box.className = 'cr-evidence';
    var head = document.createElement('p');
    head.className = 'cr-evidence-head';
    head.textContent = ev.heading;
    box.appendChild(head);
    var ul = document.createElement('ul');
    ul.className = 'cr-evidence-list';
    ev.lines.forEach(function (line) {
      var item = document.createElement('li');
      item.className = line.passed ? 'is-pass' : 'is-fail';
      item.textContent = line.text;
      ul.appendChild(item);
    });
    if (ev.more > 0) {
      var more = document.createElement('li');
      more.className = 'cr-evidence-more';
      more.textContent = '+' + ev.more + ' more';
      ul.appendChild(more);
    }
    box.appendChild(ul);
    return box;
  }

  function renderBody(sections) {
    if (!resultList) { return; }
    resultList.innerHTML = '';
    sections.forEach(function (sec) {
      var group = document.createElement('div');
      group.className = 'cr-group';
      if (sec.label) {
        var lab = document.createElement('div');
        lab.className = 'cr-group-label';
        lab.textContent = sec.label;
        group.appendChild(lab);
      }
      var ul = document.createElement('ul');
      ul.className = 'cr-rows';
      sec.rows.forEach(function (row) { ul.appendChild(renderRow(row)); });
      group.appendChild(ul);
      resultList.appendChild(group);
    });
  }

  // Pop the modal in its "running the check" state.
  function showChecking() {
    setChecking(true);
    setTone('checking');
    if (resultBadge) { resultBadge.innerHTML = '<span class="cr-spinner"></span>'; }
    if (resultTitle) { resultTitle.textContent = 'Checking your code…'; }
    if (resultSub) { resultSub.textContent = 'Running your program and comparing the results.'; }
    if (resultFoot) { resultFoot.hidden = true; }
    showLadder(false);
    if (resultList) {
      resultList.innerHTML = '';
      var box = document.createElement('div');
      box.className = 'cr-checking';
      var sp = document.createElement('span');
      sp.className = 'cr-spinner';
      box.appendChild(sp);
      box.appendChild(document.createTextNode('One moment…'));
      resultList.appendChild(box);
    }
    openModal();
  }

  // Show the graded results.
  function showResults(passed, results) {
    setChecking(false);
    var sections = resultsToSections(results);
    var flat = sections.reduce(function (a, s) { return a.concat(s.rows); }, []);
    // code-challenge-dotted-path-checkpoints: exclude BOTH hint rows AND skipped
    // rows from the "X of Y passed" tally (the top overallPassed() badge already
    // excludes skips; the per-row tally must too, or a skip inflates the count).
    // advisory-checkpoints (D2): ALSO exclude advisory rows — on a pass as well as
    // a fail. The `advisory` marker is independent of `status`, because a BUILT
    // extension renders as a normal green pass row and must still not be counted;
    // a status-only filter cannot express that. The existing `status !== 'hint'`
    // term is kept and is load-bearing: `structural` rows are 'hint' without
    // carrying the advisory marker, and dropping the term would pull them back in.
    var gating = flat.filter(function (r) {
      return !r.advisory && r.status !== 'hint' && r.status !== 'skip' && r.status !== 'advisory';
    });
    var passedCount = gating.filter(function (r) { return r.status === 'pass'; }).length;
    var totalCount = gating.length;
    var failCount = totalCount - passedCount;
    setTone(passed ? 'success' : 'fail');
    if (resultBadge) { resultBadge.innerHTML = passed ? ICON.trophy : ICON.target; }
    if (resultTitle) { resultTitle.textContent = passed ? 'Nice work — you passed!' : 'Not quite yet'; }
    if (resultSub) {
      // advisory-checkpoints (B4): pluralise. One gating check plus a menu of
      // optional ones is the NORMAL shape for this feature, so "All 1 checks
      // passed." would be the usual render, not a corner case.
      var noun = totalCount === 1 ? 'check' : 'checks';
      // code-a-game-browser-fixes (C): "no required checks" is true ONLY when the challenge has none
      // AT ALL (skipped rows count as required here). When there ARE required rows but none could
      // be evaluated (every one `skip`), say so — never tell a student their challenge is empty.
      // same as `gating` above, skipped rows included (an OPTIONAL skipped check is not required).
      var requiredAny = flat.some(function (r) {
        return !r.advisory && r.status !== 'hint' && r.status !== 'advisory';
      });
      if (!totalCount && requiredAny) {
        resultSub.textContent = 'None of the checks could run on your code yet — keep building, then check again.';
      } else if (!totalCount) {
        // Every row is advisory, so there is no gating row at all. overallPassed
        // returns false here, and the old copy read "0 of 0 checks passed — 0 to
        // fix" under a FULL progress bar. Say something true instead.
        resultSub.textContent = 'This challenge has no required checks yet — everything listed here is optional. '
          + 'Ask your teacher to take a look.';
      } else {
        resultSub.innerHTML = passed
          ? ('All <b>' + totalCount + '</b> ' + noun + ' passed.')
          : ('<b>' + passedCount + '</b> of <b>' + totalCount + '</b> ' + noun
            + ' passed — <b>' + failCount + '</b> to fix.');
      }
    }
    // With no gating row there is no ratio to draw; a full bar under "Not quite
    // there yet" is exactly the falsehood this guard removes.
    if (resultProgress && resultProgress.parentNode) { resultProgress.parentNode.hidden = !totalCount; }
    setProgress(totalCount ? passedCount / totalCount : 0);
    renderBody(sections);
    // tiered-hints: the challenge-level ladder under the rows of every FAILED result.
    showLadder(!passed);
    if (resultFoot) { resultFoot.hidden = false; }
    openModal();
    // friendlier-challenges: a graded fail counts toward "Use the solution" (a pass never resets).
    solution.noteCheck(passed);
  }

  // Show a run-failure / error state (no per-check rows).
  function showError(message) {
    setChecking(false);
    setTone('error');
    if (resultBadge) { resultBadge.innerHTML = ICON.alert; }
    if (resultTitle) { resultTitle.textContent = 'Something went wrong'; }
    if (resultSub) { resultSub.textContent = ''; }
    showLadder(false);
    if (resultList) {
      resultList.innerHTML = '';
      var p = document.createElement('p');
      p.className = 'cr-comment';
      p.style.padding = 'var(--space-2) var(--space-3)';
      p.textContent = message;
      resultList.appendChild(p);
    }
    if (resultFoot) { resultFoot.hidden = false; }
    openModal();
  }

  // Map a grading result to the POSTed shape. Temporal + expected-output rows
  // carry NO `expected`/variable-value pair — they post { kind, …, diff } (all
  // already whitelisted by the challengeCheck route). Keeps the stored attempt
  // free of the full golden/series (anti-bloat).
  //
  // advisory-checkpoints (E2): this is the INTERNAL body. The exported-to-callers
  // `postResultRow` below wraps it and adds the advisory marker — in ONE place,
  // for the same reason the grading stamp is central: after that change ANY kind
  // can carry `hintOnly`, so per-branch edits here would miss cases.
  //
  // ⚠️ There are TWO POST sites — `finishWithResults` (temporal / function-io /
  // trace / console-run) and `runCheck`'s legacy replay+snapshot tail — and BOTH
  // must go through `postResultRow`. The tail carried its own inline copy of the
  // expected-output + plain-variable branches until this was fixed; a duplicate
  // there is invisible to every automated gate and drops the advisory marker on
  // the commonest challenge shapes. Never reintroduce one.
  function buildResultRow(r) {
    if (r.kind === 'expected-output') {
      return { kind: r.kind, label: r.label, passed: r.passed, diff: r.diff };
    }
    if (isTemporalKind(r.kind)) {
      // code-challenge-dotted-path-checkpoints: path-temporal is a temporal kind;
      // carry `skipped` so a fallback-inconclusive row is stored distinctly (not a
      // false pass) in the teacher-reviewable attempt.
      return { kind: r.kind, variable: r.variable, label: r.label, passed: r.passed,
        diff: r.diff, actual: r.actual, skipped: r.skipped };
    }
    // code-challenge-behavioral-structural: behavioural + structural rows post
    // only the whitelisted result fields. `behavioralType`/`pattern` are still
    // excluded — they aren't in the challengeCheck POST joi and would drop the
    // whole row. `hintOnly` no longer is: advisory-checkpoints whitelisted it on
    // the route AND declared it on the stored sub-schema, and the wrapper below
    // adds it for every kind.
    if (r.kind === 'behavioral') {
      return { kind: r.kind, variable: r.variable, label: r.label, passed: r.passed, diff: r.diff, actual: r.actual };
    }
    if (r.kind === 'structural') {
      return { kind: r.kind, label: r.label, passed: r.passed, diff: r.diff };
    }
    // code-challenge-function-io: post only the whitelisted { kind, label,
    // passed, diff } — the simplest-failing-case hint, no case table / no actual
    // (anti-bloat, consistent with expected-output/structural).
    if (r.kind === 'function-io') {
      return { kind: r.kind, label: r.label, passed: r.passed, diff: r.diff };
    }
    // code-challenge-exception-checkpoints: post only the whitelisted { kind,
    // label, passed, diff, actual } — the observed exception/line is encoded into
    // `diff` (the challengeCheck POST joi already carries these; no new fields).
    if (needsConsolePass(r)) {
      return { kind: r.kind, label: r.label, passed: r.passed, diff: r.diff, actual: r.actual };
    }
    return { variable: r.variable, expected: r.expected, actual: r.actual, passed: r.passed, notFound: r.notFound };
  }

  // advisory-checkpoints (E2): carry the advisory marker onto the STORED attempt,
  // so the teacher's review surface can tell an unbuilt optional extension from a
  // genuine failure. Only ever set to `true` — never `false` — so existing stored
  // rows and their standing specs stay byte-identical and the payload stays lean.
  function postResultRow(r) {
    var row = buildResultRow(r);
    if (r && r.hintOnly === true && row) { row.hintOnly = true; }
    return row;
  }

  // activity-retry-and-close-on-success (Trello 2eajTgm0): offer the way back after a PASS,
  // and only once the check result has actually been stored.
  //
  // Challenge is the ONE surface of the five launched in the classroom's OWN tab, so its
  // ending is a NAVIGATION, not a close — and with no valid return address it goes BACK
  // rather than telling the student to close the tab, which would end their session.
  //
  // Its teacher test is the PREVIEW BODY ATTRIBUTE, not a `canManage` config flag: unlike
  // debug, completion and guided-make, this surface has no such flag at all.
  // m9-extend-your-game (Trello s5K8qoyn): `results` is threaded in so the countdown can tell
  // "passed having built something" from "passed having built nothing". On a challenge whose
  // requirements are all optional extensions, the student's first curiosity press of Check
  // passes on the baseline row alone — and a full-screen "Back to the course in 5…" scrim
  // offering to navigate them OUT of the activity they are meant to stay in and extend is
  // exactly the wrong thing to do at that moment.
  //
  // ⚠️ THE NEW TEST SITS AT THE `offerClose` CALL, INSIDE the `.then()`. Widening the early
  // return above to `if (!passed || preview || nothingBuilt)` is the natural way to write it
  // and it is WRONG: it skips the `result === null` branch, so a student who passed with
  // nothing built AND whose save failed would be told nothing at all — and that is the single
  // commonest path through this activity. The two conditions are independent.
  function offerAfterCheck(passed, posted, results) {
    var settled = Promise.resolve(posted).catch(function (err) { console.error(err); return null; });
    // activity-test-table (Trello 2P8y4y9S): report EVERY stored Check to the test-table chrome,
    // which shows the challenge's composite status (checkpoints AND tests). A signal only — nothing
    // else reads it, and a page with no test table has no listener.
    settled.then(function (result) {
      try {
        document.dispatchEvent(new CustomEvent('pc-challenge-checked', {
          detail: { passed: !!passed, submissionState: (result && result.submissionState) || '' }
        }));
      } catch (e) { /* a signal only — never break the Check path */ }
    });
    if (!passed || body.getAttribute('data-preview') === 'true') { return settled; }
    return settled.then(function (result) {
      // A rejected store must NOT arm anything: navigating away with the pass unrecorded is
      // the failure the awaiting exists to prevent.
      if (result === null) {
        offerReturn({ sameTab: true, warn: true, label: 'We couldn’t save that. Please tell your teacher.' });
        return;
      }
      // activity-test-table: a challenge with an attached test table is complete only when its
      // tests ALSO catch every required mistake (the server's composite gate). Checkpoints passing
      // while that half is still open must not count the student down OUT of the activity — the
      // test-table panel tells them what is left instead.
      if (result && result.submissionState && result.submissionState !== 'completed'
          && document.getElementById('test-table-config')) {
        return;
      }
      // ⚠️ NOT "the pass included no built advisory rows" on its own — that is trivially true
      // of every ordinary challenge and would suppress the countdown site-wide. The shared
      // helper's first half counts author-marked DEFINITIONS, which is zero everywhere else.
      if (shouldSuppressCloseOffer(checkpoints, results)) { return; }
      offerClose({ sameTab: true, label: 'Nice work — you passed.' });
    });
  }

  // Shared "show + POST" once a set of graded results is in hand.
  function finishWithResults(results) {
    stopStderrCapture();
    var passed = overallPassed(results);
    showResults(passed, results);
    // m9-extend-your-game: `results` is the GRADED rows, not the posted ones — the posted
    // shape drops fields, and the suppression test reads `hintOnly`/`kind`/`passed`.
    offerAfterCheck(passed, api.postChallengeCheck(courseCtx, {
      parent: attemptId,
      passed: passed,
      runFailed: false,
      results: results.map(postResultRow)
    }), results);
  }

  // checker-diagnostics B2: everything the CURRENT Check run writes to stderr (public
  // editor-core API, the debug-chrome/completion-chrome pattern). Reset at every Check start,
  // accumulated only while that Check is in flight; only a STUDENT crash ever reads it.
  var checkStderr = '';
  var capturingStderr = false;
  core.on('stderr', function (text) {
    if (!capturingStderr) { return; }
    // The worker's batched stderr delivers one LINE per chunk with no trailing newline
    // (pgzero's print_exc sites); a launch-error chunk is a whole multi-line block. Keep the
    // line structure either way so the formatter can find the frame and exception lines.
    var t = String(text == null ? '' : text);
    checkStderr += /\n$/.test(t) ? t : t + '\n';
  });
  function startStderrCapture() { checkStderr = ''; capturingStderr = true; }
  function stopStderrCapture() { capturingStderr = false; }
  // The crash sentence for a student-error, from the buffered stderr — or null (unparseable).
  function studentCrashSentence() {
    stopStderrCapture();
    return crashMessage(checkStderr, ctx.clientsideMode === 'python' ? 'program' : 'game');
  }

  var RUN_FAILURE_SENTENCE =
    'Something went wrong while running your code — check the editor for errors and try again.';

  // `sentence` is the student-crash sentence, or null for today's generic text (a timeout, a
  // runner error, or a crash whose stderr did not parse).
  function showRunFailure(sentence) {
    stopStderrCapture();
    showError(sentence || RUN_FAILURE_SENTENCE);
    // friendlier-challenges: a run-failure Check counts toward "Use the solution" too.
    solution.noteCheck(false);
    api.postChallengeCheck(courseCtx, {
      parent: attemptId, passed: false, runFailed: true, results: []
    }).catch(function (err) { console.error(err); });
  }

  function setChecking(on) {
    // step-checks: the full Check's OWN running state, read by the step-check button (never
    // checkBtn.disabled, which the step check itself sets) and the hook that clears its line.
    fullCheckRunning = !!on;
    if (on) { onFullCheckStart(); }
    if (checkBtn) { checkBtn.disabled = on || scheduleLock.enforce; }
    // friendlier-challenges: the solution buttons are disabled while a Check runs.
    solution.setChecking(on);
    // step-by-step-challenges: the last step's "Check my code" follows the toolbar Check.
    syncStepCheck();
  }

  // code-challenge-temporal-checkpoints (task 6.1/6.2): grade a Pygame Zero
  // challenge that carries any temporal checkpoint via the deterministic stepped
  // engine — NOT the wall-clock replay. One fast round-trip returns
  // { finalValues, series } (R11): legacy/expected-output checkpoints grade off
  // `finalValues`/stdout, temporal ones off `series`. The frame cap is the
  // deterministic terminus, so this never races the replay grace/watchdog timers;
  // Stop is N/A during the one-shot (U12), and the canvas shows the last drawn
  // frame with a brief "checking…" (U11).
  function runTemporalCheck() {
    showChecking();

    var studentStdout = '';
    var stdoutHandler = function (chunk) { studentStdout += (chunk == null ? '' : String(chunk)); };
    core.on('stdout', stdoutHandler);

    var settled = false;
    var stateHandler = null;
    var timer = null;
    function clearTimer() { if (timer) { clearTimeout(timer); timer = null; } }
    // code-challenge-dotted-path-checkpoints: the naming-variety fallback can add a
    // SECOND full stepped-run+settrace round trip. Give it its OWN fresh timeout
    // window (re-armed before the discovery call) rather than sharing call 1's
    // budget — a Check that needs the fallback honestly costs up to ~2x as long.
    function armTimer() {
      clearTimer();
      timer = setTimeout(function () { runFailed('timeout'); }, TEMPORAL_CHECK_TIMEOUT_MS);
    }
    function cleanup() {
      settled = true;
      core.off('stdout', stdoutHandler);
      if (stateHandler) { core.off('state', stateHandler); stateHandler = null; }
      clearTimer();
    }
    // checker-diagnostics B2: `cause` is 'student-error' | 'runner-error' | 'timeout'. Only a
    // student crash reads the buffered stderr; the other two keep today's sentence verbatim.
    function runFailed(cause) {
      if (settled) { return; }
      cleanup();
      var sentence = cause === 'student-error' ? studentCrashSentence() : null;
      core.stop();
      showRunFailure(sentence);
    }
    // A student error / boot failure never resolves requestChallengeStep, so a
    // terminal-state listener + a timeout convert it to a friendly run failure.
    stateHandler = function (state) {
      if (state === 'student-error' || state === 'runner-error') { runFailed(state); }
    };
    core.on('state', stateHandler);
    armTimer();

    // Snapshot ALL variable checkpoints (temporal + legacy) so finalValues carries
    // every non-temporal variable too (R11); expected-output ones have no variable.
    // code-challenge-collision-oscillation-liveness: a `relational-series`
    // checkpoint has NO single `variable` — its actors live inside `expr`. Collect
    // those var names too so the stepped driver samples every referenced series
    // (else the derived collision/gap/ratio series would be empty → skipped).
    // code-challenge-dotted-path-checkpoints: a path-temporal checkpoint's `path`
    // is collected here too (resolved per-frame by the runner's path resolver).
    var variables = collectObservedVariables(checkpoints);

    function seriesHasNonNull(arr) {
      return Array.isArray(arr) && arr.some(function (v) { return v !== null && v !== undefined; });
    }

    // Finish: grade every checkpoint from the assembled passData in one shot.
    function grade(finalValues, passData) {
      cleanup();
      core.stop();
      finishWithResults(compareAll(checkpoints, finalValues || {}, studentStdout, passData));
    }

    core.requestChallengeStep(variables, frameCap, recordedInputs, challengeSeed).then(function (step) {
      if (settled) { return; }
      var finalValues = step.finalValues || {};
      var passData = {
        valueSeries: step.series || {},
        // code-challenge-flag-state-checkpoints: the discrete (bool/string) series
        // for flag-becomes / state-sequence kinds. The `variables` list already
        // includes every cp.variable, so their vars are sampled for free.
        discreteSeries: step.discreteSeries || {}
      };

      // code-challenge-dotted-path-checkpoints: which path-temporal checkpoints did
      // NOT resolve literally on THIS student's run (series absent or entirely
      // null)? This is NEW orchestration (no existing precedent structurally fits —
      // runConsoleCheck is python-mode-only and never runs for path-temporal, and
      // its passes are statically decided, never conditional on a prior run's
      // result the way this fallback is).
      var needFallback = checkpoints.filter(function (cp) {
        return cp.kind === 'path-temporal' &&
          !(seriesHasNonNull(passData.valueSeries[cp.path]) ||
            seriesHasNonNull(passData.discreteSeries[cp.path]));
      });

      if (!needFallback.length) { grade(finalValues, passData); return; }

      // ONE additional discovery run against the student's own code, SHARED across
      // every checkpoint needing fallback in this Check, on its own fresh timeout.
      armTimer();
      core.requestChallengeStep(variables, frameCap, recordedInputs, challengeSeed, true)
        .then(function (step2) {
          if (settled) { return; }
          var discovery = {
            discoveredPaths: step2.discoveredPaths || [],
            series: step2.series || {},
            discreteSeries: step2.discreteSeries || {}
          };
          passData.skippedPaths = {};
          needFallback.forEach(function (cp) {
            var resolved = resolvePathFallback(cp, discovery, undefined, isDiscreteKind);
            if (resolved && resolved.path) {
              // Merge the matched candidate's series under the checkpoint's OWN
              // path key — transparent to comparePathTemporal.
              passData.valueSeries[cp.path] = discovery.series[resolved.path];
              passData.discreteSeries[cp.path] = discovery.discreteSeries[resolved.path];
            } else {
              passData.skippedPaths[cp.path] = true; // inconclusive → skipped row
            }
          });
          grade(finalValues, passData);
        }).catch(function (err) {
          console.error(err);
          runFailed('runner-error');
        });
    }).catch(function (err) {
      console.error(err);
      runFailed('runner-error');
    });
  }

  // code-challenge-exception-checkpoints (grading v3 [2/3], design D5 / ADDENDUM
  // B): the unified console-mode Check. Supersedes the former separate
  // runFunctionIOCheck / runTraceCheck one-primary-plus-secondary flows — those
  // had NO console-run awareness, and since runs-clean auto-ticks whenever the
  // reference ran clean, console-run coexists with function-io / expected-output /
  // behavioural in the COMMON case, so a priority if-chain would silently misgrade
  // the other family. Instead: COMPUTE which passes the present checkpoint kinds
  // need, RUN each needed pass (console-run possibly several times, one per
  // distinct triggerStdin group), accumulate every result into ONE passData, then
  // call compareAll ONCE. Each kind still grades ONLY from its own family's blob
  // (function-io ← fncall; behavioural/structural ← trace, which also serves
  // expected-output/plain-variable; console-run ← the canned-stdin pass; else the
  // legacy replay/snapshot). All passes are VISIBLE runs (stdout streams live).
  // Mirrors runTemporalCheck's terminal-state + timeout guards.
  function runConsoleCheck() {
    showChecking();

    function isPlainVariable(cp) {
      return cp && cp.variable && cp.kind !== 'expected-output' && cp.kind !== 'function-io' &&
        cp.kind !== 'behavioral' && cp.kind !== 'structural' && !needsConsolePass(cp) &&
        !isTemporalKind(cp.kind);
    }

    var needFncall = checkpoints.some(function (cp) { return cp.kind === 'function-io'; });
    var needTrace = checkpoints.some(function (cp) {
      return cp.kind === 'behavioral' || cp.kind === 'structural';
    });
    var needConsoleRun = checkpoints.some(needsConsolePass);
    // expected-output + plain-variable need finalValues + stdout. The trace pass
    // already supplies both; only fall back to the legacy wall-clock replay +
    // snapshot when NO trace pass runs.
    var needVarStdout = checkpoints.some(function (cp) {
      return cp.kind === 'expected-output' || isPlainVariable(cp);
    });
    var needLegacy = needVarStdout && !needTrace;

    // Accumulated across the needed passes; graded in one shot at the end.
    var passData = {};
    var finalValues = {};
    var stdout = '';

    var settled = false;
    var stateHandler = null;
    var timer = null;
    function cleanup() {
      settled = true;
      if (stateHandler) { core.off('state', stateHandler); stateHandler = null; }
      if (timer) { clearTimeout(timer); timer = null; }
    }
    // checker-diagnostics B2: the console multi-pass path never sees a student crash (each
    // pass captures it), so every cause here ('runner-error' | 'timeout', named at the call
    // sites for symmetry with runTemporalCheck) keeps today's sentence.
    function runFailed() {
      if (settled) { return; }
      cleanup();
      core.stop();
      showRunFailure(null);
    }
    // A harness/boot failure never resolves a request*, so a terminal-state
    // listener + a timeout convert it to a friendly run failure. (Student crashes
    // do NOT reach these states — the fncall/trace/console-run passes CAPTURE them
    // and still resolve; the legacy replay reports them via its own outcome.)
    stateHandler = function (state) {
      if (state === 'runner-error') { runFailed('runner-error'); }
    };
    core.on('state', stateHandler);
    timer = setTimeout(function () { runFailed('timeout'); }, TEMPORAL_CHECK_TIMEOUT_MS);

    // --- One thunk per pass; each returns a promise and populates passData ------
    function runFncallPass() {
      var calls = [];
      checkpoints.forEach(function (cp) {
        if (cp.kind === 'function-io' && cp.functionName && Array.isArray(cp.cases)) {
          cp.cases.forEach(function (kase) {
            // function-io-stdin: typed answers ride along when the case has them.
            calls.push(fnCallFor(cp.functionName, kase));
          });
        }
      });
      return core.requestFunctionCall(calls, challengeSeed).then(function (fncall) {
        passData.fncallData = { results: fncall.results || [], functions: fncall.functions || [] };
      });
    }

    function runTracePass() {
      var traceVars = checkpoints
        .filter(function (cp) { return cp.variable; })
        .map(function (cp) { return cp.variable; });
      return core.requestChallengeTrace(traceVars, recordedInputs, challengeSeed).then(function (trace) {
        passData.traceData = { behavioral: trace.behavioral || {}, structural: trace.structural || {} };
        finalValues = trace.finalValues || {};
        stdout = trace.stdout || '';
      });
    }

    // One console-run per distinct triggerStdin group (checkpoints sharing a stdin
    // scenario share the one run). Keyed identically to the grader's lookup.
    // step-checks: the per-stdin loop itself is the shared `runConsoleRuns` below.
    function runConsoleRunPasses() {
      var stdins = [];
      checkpoints.forEach(function (cp) {
        // console-run kinds contribute their single triggerStdin scenario.
        if (isConsoleRunKind(cp.kind)) { stdins.push(cp.triggerStdin); }
        // code-challenge-stdin-transcript: enumerate EVERY caseTable row's stdin —
        // each distinct stdin runs the pass once, keyed identically to the grader's
        // per-case byStdin lookup. A stdin shared with a console-run kind collapses
        // to one run.
        if (cp.kind === 'stdin-transcript' && Array.isArray(cp.caseTable)) {
          cp.caseTable.forEach(function (row) { stdins.push(row && row.stdin); });
        }
      });
      return runConsoleRuns(stdins).then(function (data) {
        passData.consoleRunData = data;
      });
    }

    function runLegacyPass() {
      var legacyStdout = '';
      var stdoutHandler = function (chunk) { legacyStdout += (chunk == null ? '' : String(chunk)); };
      core.on('stdout', stdoutHandler);
      return replay(core, ctx.clientsideMode, recordedInputs, challengeSeed).then(function (outcome) {
        core.off('stdout', stdoutHandler);
        core.stop();
        if (outcome.outcome === 'error' || outcome.outcome === 'stopped') {
          // Surface as a friendly run failure via the shared reject path.
          var e = new Error('run-' + outcome.outcome);
          e.isRunFailure = true;
          throw e;
        }
        var legacyVars = checkpoints.filter(isPlainVariable).map(function (cp) { return cp.variable; });
        return core.requestSnapshot(legacyVars).then(function (values) {
          finalValues = values || {};
          stdout = legacyStdout;
        });
      });
    }

    // Chain the needed passes sequentially (each is a fresh visible run that
    // resolves on its own `finished`, so no explicit stop between them), then
    // grade everything at once.
    var chain = Promise.resolve();
    if (needFncall) { chain = chain.then(function () { if (!settled) { return runFncallPass(); } }); }
    if (needTrace) { chain = chain.then(function () { if (!settled) { return runTracePass(); } }); }
    if (needConsoleRun) { chain = chain.then(function () { if (!settled) { return runConsoleRunPasses(); } }); }
    if (needLegacy) { chain = chain.then(function () { if (!settled) { return runLegacyPass(); } }); }

    chain.then(function () {
      if (settled) { return; }
      cleanup();
      core.stop();
      var results = compareAll(checkpoints, finalValues, stdout, passData);
      finishWithResults(results);
    }).catch(function (err) {
      console.error(err);
      runFailed('runner-error');
    });
  }

  // step-checks (Trello IeSYCMgQ): the per-stdin console-run loop, shared by the full Check's
  // console pass and "Check this step". Each DISTINCT stdin (keyed exactly as the grader's
  // byStdin lookup) runs once, sequentially, as a visible run; resolves `{ byStdin }`. The
  // optional `cancelled()` is read before each run — the full Check passes none (unchanged).
  function runConsoleRuns(stdinList, cancelled) {
    var groups = {};
    (stdinList || []).forEach(function (stdin) {
      var key = consoleRunStdinKey(stdin);
      if (!Object.prototype.hasOwnProperty.call(groups, key)) {
        groups[key] = Array.isArray(stdin) ? stdin : [];
      }
    });
    var byStdin = {};
    return Object.keys(groups).reduce(function (p, key) {
      return p.then(function () {
        if (cancelled && cancelled()) { return; }
        return core.requestConsoleRun(groups[key], challengeSeed).then(function (run) {
          byStdin[key] = run;
        });
      });
    }, Promise.resolve()).then(function () { return { byStdin: byStdin }; });
  }

  // step-checks: "Check this step" (step view only). Grades the rows of EVERY stdin-transcript
  // checkpoint tagged `step <= N` with gradeStepCheck, LOCALLY: no POST, no modal, never the
  // outer setChecking, never solution.noteCheck, and no full-Check state (passData) is written.
  // Resolves the stepCheckMessage `{ tone, lines }` (or null). While it runs the toolbar Check
  // and "Check my code" are disabled directly, "Use the solution" through solution.setChecking,
  // and the runner's own controls are fenced (find-the-bug-chrome.js's precedent).
  var stepCheckBusy = false;
  var STEP_RUNNER_CONTROL_IDS = ['runner-run', 'runner-stop', 'stage-prerun-btn'];
  function stepTranscriptCheckpoints() {
    return checkpoints.filter(function (cp) {
      return !!cp && cp.kind === 'stdin-transcript' && Array.isArray(cp.caseTable);
    });
  }
  function rowStep(row) {
    return row && Number.isInteger(row.step) ? row.step : null;
  }
  // step-checks-prompts-functions (Trello EiSNIhrA): prompt entries live on stdin-transcript
  // checkpoints (`stepPrompts: [{ step, text, stdin? }]`), function checks are function-io
  // cases with an integer `step`.
  function stepPromptEntries(cp) {
    return Array.isArray(cp.stepPrompts) ? cp.stepPrompts.filter(Boolean) : [];
  }
  function stepFunctionCheckpoints() {
    return checkpoints.filter(function (cp) {
      return !!cp && cp.kind === 'function-io' && !!cp.functionName && Array.isArray(cp.cases);
    });
  }
  // The button exists only on a step with >= 1 check tagged EXACTLY that step (1-based):
  // a caseTable row or a prompt entry (console runs) or a function-io case (function calls).
  function stepHasCheck(step) {
    if (hasConsoleRun && stepTranscriptCheckpoints().some(function (cp) {
      return cp.caseTable.some(function (row) { return rowStep(row) === step; }) ||
        stepPromptEntries(cp).some(function (e) { return rowStep(e) === step; });
    })) { return true; }
    return hasFunctionIO && stepFunctionCheckpoints().some(function (cp) {
      return cp.cases.some(function (kase) { return rowStep(kase) === step; });
    });
  }
  function runStepCheck(step) {
    var cps = hasConsoleRun ? stepTranscriptCheckpoints() : [];
    var fnCps = hasFunctionIO ? stepFunctionCheckpoints() : [];
    // Rows and prompt probes share ONE runConsoleRuns (a probe whose stdin equals a row's
    // collapses into that row's run); each prompt is graded on its own probe's key (A1).
    var stdins = [];
    cps.forEach(function (cp) {
      cp.caseTable.forEach(function (row) {
        var s = rowStep(row);
        if (s !== null && s <= step) { stdins.push(row.stdin); }
      });
      stepPromptEntries(cp).forEach(function (e) {
        var s = rowStep(e);
        if (s !== null && s <= step) { stdins.push(Array.isArray(e.stdin) ? e.stdin : []); }
      });
    });
    // The function calls: every function-io case tagged `step <= N`, the full Check's call
    // shape (`runFncallPass`). The grader matches results by name + args + stdin (D3, A8).
    var calls = [];
    fnCps.forEach(function (cp) {
      cp.cases.forEach(function (kase) {
        var s = rowStep(kase);
        if (s !== null && s <= step) {
          calls.push(fnCallFor(cp.functionName, kase));
        }
      });
    });

    stepCheckBusy = true;
    if (checkBtn) { checkBtn.disabled = true; }
    solution.setChecking(true);
    syncStepCheck();

    // The runner controls' hand-back: what editor-core last set (it calls setButtons right
    // BEFORE every 'state' / 'ready' emit, so it is re-read there before the fence re-applies).
    var handBack = {};
    function byId(id) { return document.getElementById(id); }
    function readRunnerButtons() {
      var r = byId('runner-run');
      var s = byId('runner-stop');
      if (r) { handBack['runner-run'] = !!r.disabled; }
      if (s) { handBack['runner-stop'] = !!s.disabled; }
    }
    function fence() {
      STEP_RUNNER_CONTROL_IDS.forEach(function (id) {
        var b = byId(id);
        if (b) { b.disabled = true; }
      });
    }
    readRunnerButtons();
    var pre = byId('stage-prerun-btn');
    if (pre) { handBack['stage-prerun-btn'] = !!pre.disabled; }
    fence();
    function guard(e) {
      if (!stepCheckBusy) { return; }
      var t = e.target && e.target.closest
        ? e.target.closest('#runner-run, #runner-stop, #stage-prerun-btn') : null;
      if (t) { e.preventDefault(); e.stopPropagation(); }
    }
    document.addEventListener('click', guard, true);

    return new Promise(function (resolve) {
      var settled = false;
      var timer = null;
      // ONE 'state' handler: re-read + re-fence, and the full Check's runner-error guard.
      function onState(state) {
        if (settled) { return; }
        readRunnerButtons();
        fence();
        if (state === 'runner-error') { failed(STEP_CHECK_RUNNER_ERROR_LINE); }
      }
      function onReady() {
        if (settled) { return; }
        readRunnerButtons();
        // stage-prerun.js derives its button from Run on 'ready'.
        var r = byId('runner-run');
        if (r && Object.prototype.hasOwnProperty.call(handBack, 'stage-prerun-btn')) {
          handBack['stage-prerun-btn'] = handBack['runner-run'] === true ||
            (typeof r.getClientRects === 'function' && r.getClientRects().length === 0);
        }
        fence();
      }
      function release() {
        settled = true;
        if (timer) { clearTimeout(timer); timer = null; }
        core.off('state', onState);
        core.off('ready', onReady);
        document.removeEventListener('click', guard, true);
        Object.keys(handBack).forEach(function (id) {
          var b = byId(id);
          if (b) { b.disabled = handBack[id]; }
        });
        stepCheckBusy = false;
        if (checkBtn) { checkBtn.disabled = fullCheckRunning || scheduleLock.enforce; }
        solution.setChecking(false);
        syncStepCheck();
      }
      // A hung or failed run: the full Check's guard, never showRunFailure; stop the run
      // exactly as runFailed does and run no further canned rows.
      function failed(line) {
        if (settled) { return; }
        release();
        core.stop();
        resolve({ tone: 'fail', lines: [line] });
      }
      core.on('state', onState);
      core.on('ready', onReady);
      timer = setTimeout(function () { failed(STEP_CHECK_TIMEOUT_LINE); }, TEMPORAL_CHECK_TIMEOUT_MS);

      // Sequential passes under the ONE guard/timer above: console runs (rows + probes),
      // then the function calls (only when a function case is tagged `step <= N`).
      var consoleData = null;
      var fncallData = null;
      runConsoleRuns(stdins, function () { return settled; }).then(function (data) {
        consoleData = data;
        if (settled || calls.length === 0) { return; }
        return core.requestFunctionCall(calls, challengeSeed).then(function (fncall) {
          fncallData = { results: (fncall && fncall.results) || [], functions: (fncall && fncall.functions) || [] };
        });
      }).then(function () {
        if (settled) { return; }
        release();
        var message = null;
        try {
          var results = [];
          cps.forEach(function (cp) {
            results.push(gradeStepCheck(cp, consoleData, step));
            results.push(gradePromptStepCheck(cp, consoleData, step));
          });
          if (fncallData) {
            fnCps.forEach(function (cp) { results.push(gradeFunctionStepCheck(cp, fncallData, step)); });
          }
          message = stepCheckMessage(results, step);
        } catch (err) {
          console.error(err);
          message = { tone: 'fail', lines: [STEP_CHECK_RUNNER_ERROR_LINE] };
        }
        resolve(message);
      }).catch(function (err) {
        console.error(err);
        failed(STEP_CHECK_RUNNER_ERROR_LINE);
      });
    });
  }

  function runCheck() {
    if (!checkBtn) { return; }
    // class-due-dates: a closed (past due + locked) challenge never runs a Check for a real
    // student — the server would refuse the result anyway (schedule.challengeCheck).
    if (scheduleLock.enforce) { return; }
    // step-checks: never start a full Check while "Check this step" is running.
    if (stepCheckBusy) { return; }
    showChecking();
    // checker-diagnostics B2: a fresh stderr buffer for THIS Check run.
    startStderrCapture();

    // code-challenge-temporal-checkpoints: pgzero temporal challenges use the
    // stepped engine; everything else keeps the visible wall-clock replay.
    if (hasTemporal) {
      runTemporalCheck();
      return;
    }

    // code-challenge-function-io / behavioral-structural / exception-checkpoints:
    // a console (`python`) challenge with any function-io, behavioural/structural,
    // or console-run (runs-clean/raises-on/handles-gracefully) checkpoint uses the
    // unified runConsoleCheck() — it computes and runs EVERY required pass and
    // merges into one passData, so coexisting families all grade correctly (design
    // D5 / ADDENDUM B). Only the pure expected-output / plain-variable case (none
    // of these gates set) falls through to the legacy replay+snapshot path below.
    if (hasFunctionIO || hasTrace || hasConsoleRun) {
      runConsoleCheck();
      return;
    }

    // code-challenge-expected-output: accumulate the student's FULL console
    // stdout stream across the replay by verbatim concatenation (matching
    // editor-core's appendConsole — no injected separators), the same way the
    // teacher's golden was captured, so the compare is symmetric. Includes any
    // input() prompt/echo parts (the runner emits those as stdout). Subscribed
    // BEFORE the replay boots its fresh worker; unsubscribed once the outcome is
    // handled so no listener leaks across Checks. Console runs are captured to
    // natural finish (challenge-replay.js §E gates the 600ms grace off `python`).
    var studentStdout = '';
    var stdoutHandler = function (chunk) {
      studentStdout += (chunk == null ? '' : String(chunk));
    };
    core.on('stdout', stdoutHandler);
    var stdoutStopped = false;
    function stopStdoutCapture() {
      if (stdoutStopped) { return; }
      stdoutStopped = true;
      core.off('stdout', stdoutHandler);
    }

    replay(core, ctx.clientsideMode, recordedInputs, challengeSeed).then(function (outcome) {
      // Tear down the replay's run unconditionally once the outcome is decided
      // (pass, fail, or run-failure) — for pgzero/Pygame the 'finished' outcome
      // above is often reached via challenge-replay.js's grace-period timer,
      // which only resolves a promise and never itself calls core.stop(); without
      // this, the game (and any audio) would keep running quietly in the
      // background until the student's next Run/Check or they navigate away.
      // Matches the pattern already used by challenge-record-chrome.js (Stop
      // button + its own 15s timeout both call core.stop()). Safe no-op if the
      // run already legitimately ended on its own.
      core.stop();

      if (outcome.outcome === 'stopped') {
        // Aborted attempt — no result recorded at all (decisions.md).
        stopStdoutCapture();
        setChecking(false);
        hideResults();
        return;
      }
      if (outcome.outcome === 'error') {
        stopStdoutCapture();
        // checker-diagnostics B2: name the crash line ONLY for a real student
        // error. A runner-error or a watchdog timeout ignores the buffer
        // (decisions.md Round 3) and keeps today's sentence verbatim.
        showError((outcome.cause === 'student-error' && studentCrashSentence()) || RUN_FAILURE_SENTENCE);
        // friendlier-challenges: the legacy path's run failure counts toward the offer as well.
        solution.noteCheck(false);
        // A run failure DOES still POST a result (decisions.md task 6.3).
        api.postChallengeCheck(courseCtx, {
          parent: attemptId,
          passed: false,
          runFailed: true,
          results: []
        }).catch(function (err) { console.error(err); });
        return;
      }

      // outcome.outcome === 'finished' -> compare.
      // Snapshot only VARIABLE checkpoints — expected-output ones have no
      // `variable` and are graded from the captured stdout instead.
      var variables = checkpoints
        .filter(function (cp) { return cp.kind !== 'expected-output' && cp.variable; })
        .map(function (cp) { return cp.variable; });
      return core.requestSnapshot(variables).then(function (values) {
        stopStdoutCapture();
        var results = compareAll(checkpoints, values, studentStdout);
        var passed = overallPassed(results);
        showResults(passed, results);

        // activity-retry-and-close-on-success: the SECOND passing exit. Both go through the
        // same helper, which awaits the store before offering anything.
        offerAfterCheck(passed, api.postChallengeCheck(courseCtx, {
          parent: attemptId,
          passed: passed,
          runFailed: false,
          // advisory-checkpoints (§REVISION E2): this legacy replay+snapshot tail is
          // the SECOND POST site, and it used to duplicate the row builder inline —
          // an expected-output branch plus a plain-variable default, both predating
          // the advisory marker. Every challenge that is NOT temporal, function-io,
          // behavioural/structural or console-run falls here, which is the original
          // and still-common shape, so the duplicate silently stored those rows
          // WITHOUT `hintOnly` and the teacher's review panel painted a satisfied
          // advisory row red beside a pass. It now shares the ONE builder, which is
          // the whole point of E2's "add the flag in a wrapper, not per branch".
          // `postResultRow`'s expected-output and default branches emit exactly the
          // keys the inline versions did; every key any OTHER branch can emit
          // (`kind`/`label`/`diff`/`actual`/`skipped`/`hintOnly`) is whitelisted by
          // the challengeCheck `results` Joi, so no shape reachable here can trip
          // the fail-closed parser.
          results: results.map(postResultRow)
        }), results);
        // m9-extend-your-game: ⚠️ THIS SECOND CALL SITE MUST BE FIXED TOO, and nothing you can
        // run will tell you if it is not. Neither M9.18 nor M4.42 reaches this tail — both
        // grade entirely through the temporal path — so a fix applied only at the obvious site
        // passes every gate AND every browser regression check. Same two-path trap the
        // advisory-checkpoints change hit with the two POST sites above. The specs in
        // test/editor/challenge-chrome.test.js are the only verification this path gets.
      });
    }).catch(function (err) {
      // Defensive: if replay() or the requestSnapshot promise rejects, the
      // outcome-handling paths above never run, so their stopStdoutCapture() +
      // setChecking(false) calls are skipped. Log here; the trailing cleanup
      // below then unsubscribes the 'stdout' handler (idempotent) and re-enables
      // the Check button so a rejection can't leak a listener across Checks or
      // wedge the button permanently.
      console.error(err);
    }).then(function () {
      // Runs on ANY completion (resolve OR reject) — the resolved paths already
      // called these, but stopStdoutCapture() is guarded (idempotent) and
      // setChecking(false) is harmless to repeat.
      stopStdoutCapture();
      stopStderrCapture();
      setChecking(false);
    });
  }

  // class-due-dates: the page stays fully viewable and editable (draft saving is never
  // gated) but Check is disabled for a real student, with the banner as its message. A
  // teacher / course-staff session sees the same banner and keeps Check live.
  if (scheduleLock.enforce) {
    disableForLock(document, checkBtn);
    syncStepCheck();
  }
  if (checkBtn) {
    checkBtn.addEventListener('click', runCheck);
  }

  // Results modal: Close / Back dismiss it; "Check again" re-runs; backdrop click
  // and Escape also dismiss (never while a check is in flight).
  if (resultClose) { resultClose.addEventListener('click', hideResults); }
  if (resultBack) { resultBack.addEventListener('click', hideResults); }
  if (resultAgain) { resultAgain.addEventListener('click', function () { hideResults(); runCheck(); }); }
  if (resultSection) {
    resultSection.addEventListener('click', function (e) {
      if (e.target === resultSection && !resultSection.classList.contains('is-checking')) { hideResults(); }
    });
  }
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && resultSection && !resultSection.hidden && !resultSection.classList.contains('is-checking')) {
      hideResults();
    }
  });

  // Optional Assets panel (when features.assets + the template renders the controls).
  wireAssets(ctx);
}
